/**
 * Kingspan Urban Tank configurator UI
 * Product-page layout matched to kingspanwatertanks.com.au "build your own"
 * PDPs: gallery left + cascading option form right (WCPA-style).
 */

/** Live form visibility rules (expivi-tank-configurator-form.json) */
const FORM_SHOW = {
  diameter: (v) => v.type === "Round",
  width: (v) => v.type === "Slimline" || v.type === "Modline",
  length: (v) => v.type === "Slimline" || v.type === "Modline",
  sWidth: (v) => v.type === "Square",
  height: () => true,
};

/**
 * Per–tank-type PDP content. When `lockedType` is set on KingspanTankConfig,
 * the type picker is hidden and this copy drives the page layout.
 */
const TYPE_PAGES = {
  slimline: {
    key: "slimline",
    name: "Slimline",
    title: "Build Your Own Slimline Tank",
    basePrice: 890,
    sizeSummary: "Widths from 550–1150 mm · Lengths from 1200–4000 mm · Heights from 580–2470 mm",
    descriptionHtml: `
      <p>Configure a Kingspan Slimline urban tank to suit narrow sites and wall-hugging installs.
      Choose delivery state, Aquaplate colour, cascade dimensions, fittings and pumps.
      Only price-list valid width → length → height combinations are offered.</p>
      <ul>
        <li>Space-saving slimline footprint for tight side paths and courtyards</li>
        <li>State-specific Aquaplate colour charts (base + painted)</li>
        <li>Optional inlets, overflows, outlets, fire fittings and a pump (Evolution MKIV, garden or Claytech)</li>
        <li>Bluescope AQUAPLATE® polymer-coated steel construction</li>
      </ul>`,
  },
  modline: {
    key: "modline",
    name: "Modline",
    title: "Build Your Own Modline Tank",
    basePrice: 990,
    sizeSummary: "Widths from 700–1150 mm · Lengths from 1200–4000 mm · Heights from 580–2470 mm",
    descriptionHtml: `
      <p>Configure a Kingspan Modline tank — the modular rectangular option for larger
      urban capacity without a round footprint. Cascade sizing follows the Urban Retail
      Price List so every combination is manufacturable.</p>
      <ul>
        <li>Rectangular modular profile for higher volume on compact sites</li>
        <li>Width → length → height cascade with live capacity estimates</li>
        <li>Region colour charts, fittings positions and pump add-ons</li>
        <li>Food-grade polymer-coated steel with long service life</li>
      </ul>`,
  },
  round: {
    key: "round",
    name: "Round",
    title: "Build Your Own Round Tank",
    basePrice: 1200,
    sizeSummary: "Diameters from 1150–3500 mm · Heights from 725–3010 mm",
    descriptionHtml: `
      <p>Configure a Kingspan Round urban tank. Select delivery state (NSW/ACT, VIC or QLD),
      Aquaplate colour, then diameter and height. Round tanks are not offered for SA delivery.</p>
      <ul>
        <li>Classic cylindrical profile with height cascade from diameter</li>
        <li>Clock-face inlet and pump position options (1–12)</li>
        <li>Fire fittings, highflow overflows and pumps available</li>
        <li>Bluescope AQUAPLATE® coated steel for strength and durability</li>
      </ul>`,
  },
  square: {
    key: "square",
    name: "Square",
    title: "Build Your Own Square Tank",
    basePrice: 1100,
    sizeSummary: "Widths from 1000–1800 mm · Heights from 580–2470 mm",
    descriptionHtml: `
      <p>Configure a Kingspan Square urban tank for balanced footprints where width equals
      length. Choose state, colour, width and height — fittings and add-ons follow the same
      urban catalogue as other tank types.</p>
      <ul>
        <li>Equal-sided square footprint for orderly site layouts</li>
        <li>Width → height cascade with live capacity and weight</li>
        <li>Corner-friendly inlet/pump positions (A–H)</li>
        <li>Optional pump (Evolution MKIV, garden or Claytech) and fire fittings</li>
      </ul>`,
  },
};

const shopifyCfg = window.KingspanTankConfig || {};

const ACCORDION_DEFAULTS = {
  fittings: true,
  pump: false,
};

const state = {
  data: null,
  sizeRules: null,
  skuMap: null,
  values: {},
  qty: 1,
  lockedType: null, // slimline | modline | round | square
  /** When set, ATC replaces this cart group instead of appending */
  edit: null, // { groupId, lineKey } | null
  /** Persist <details> open/closed across form re-renders */
  accordions: { ...ACCORDION_DEFAULTS },
  /** Curated deep link (?preset=…): show a summary card instead of the form
      until the customer clicks "Edit this tank". { name, editing, asked } | null
      (asked: the type and size the link named, for guardPresetSize()) */
  preset: null,
  /** Postcode delivery estimate: { postcode, zone, price } | { postcode,
      notServiced: true } | null. Shown as an estimate line, never in Total —
      freight is confirmed with the order (F2: checkout may charge its own rate). */
  delivery: null,
  deliveryZones: null,
  /** Optional fittings set aside by "I'll Choose Later", restored by "Now". */
  fittingsStash: null,
  /** A painted colour dropped only because the size can't be painted: brought
      back when the shopper returns to a paintable size (ensureValidSelections). */
  paintedWish: null,
  /** Why the colour (or the fire fitting) just changed by itself, shown next to
      the field the shopper changed: { text (HTML), field } | null. Cleared by
      the next choice. */
  paintNotice: null,
  /** The size field the shopper just changed (where a new paint notice shows). */
  noticeField: null,
  firefitNotice: "",
};

function captureAccordionState(root = document.getElementById("builderForm")) {
  if (!root) return;
  root.querySelectorAll("details[data-kut-accordion]").forEach((el) => {
    const id = el.dataset.kutAccordion;
    if (id) state.accordions[id] = el.open;
  });
}

function isAccordionOpen(id) {
  if (Object.prototype.hasOwnProperty.call(state.accordions, id)) {
    return !!state.accordions[id];
  }
  return !!ACCORDION_DEFAULTS[id];
}

function newConfigGroupId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `kut_${crypto.randomUUID()}`;
  }
  return `kut_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function lockedTypePage() {
  return state.lockedType ? TYPE_PAGES[state.lockedType] || null : null;
}

function resolveLockedTypeKey(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  if (TYPE_PAGES[s]) return s;
  const byName = Object.values(TYPE_PAGES).find((p) => p.name.toLowerCase() === s);
  return byName?.key || null;
}

/**
 * The six pumps sold with a tank, in KingspanTankCore.PUMPS order — the order
 * of the pump chips, of cart lines and of the pump offer (the first that
 * qualifies is the discounted one): Evolution MKIV, Integrated Garden Pump
 * System, Claytech C3X, C3X + Mains Switching, C5X, C5X + Mains Switching.
 * ONE pump per tank: choosing one clears the others (setValue); an old link or
 * cart line with two keeps the first in this order (ensureValidSelections).
 */
const PUMP_FIELDS = ((window.KingspanTankCore && window.KingspanTankCore.PUMP_FIELDS) || []).slice();

/**
 * Shopify nested cart lines: with `enabled`, each add-on line is sent with
 * `parent_id` (the tank's variant id, same /cart/add.js request) so the cart,
 * checkout, emails and admin show it under its tank. OFF until the staging
 * probe (tester round 2, S0: V1–V9) passes. This is the core's switch
 * (KingspanTankCore.CART_NESTING, assets/kingspan-tank-core.js), the ONE
 * switch the chat builder reads too: turn it on there, not here. With it off
 * every line is flat, exactly as before; the cart groups a build's rows by
 * _kut_group either way.
 */
const CART_NESTING = (window.KingspanTankCore && window.KingspanTankCore.CART_NESTING) || Object.seal({ enabled: false });

const TRACKED_ADDON_FIELDS = [
  "inlet2",
  "overflow2",
  "outlet2",
  "outlet3",
  "highflow1",
  "highflow2",
  ...PUMP_FIELDS,
];

/** Add-ons the builder no longer sells (Grundfos, the CLA-CX, the flush
    diverter, the Acquasaver). Their sku-map entries and variants stay for old
    orders. Old deep links can't set them (not in DEEP_LINK_KEYS) and
    loadEditFromCart drops them from an old line's _tank_config. */
const RETIRED_ADDON_FIELDS = [
  "grundfosScala",
  "claytechCLACX20mmAcqua5m",
  "flushDiverter",
  "acquasaver",
];

const ADDON_LABELS = {
  inlet2: "Extra Inlet #2",
  overflow2: "Extra Overflow #2",
  outlet2: "Extra Outlet #2",
  outlet3: "Outlet #3",
  highflow1: "Highflow for Overflow #1",
  highflow2: "Highflow for Overflow #2",
  mk3Pump: "Submersible Pump — Evolution MKIV",
  integratedGardenPump: "Integrated Garden Pump System",
  claytechC3X: "Claytech C3X",
  claytechC3X20mmAcqua: "Claytech C3X + Mains Switching",
  claytechC5X: "Claytech C5X",
  claytechC5X20mmAcqua: "Claytech C5X + Mains Switching",
  firefit: "Fire Fitting",
  firefit3: "Fire Fitting for Outlet #3",
  paintedColour: "Painted Colour Surcharge",
  paintedColour2: "Painted Colour Surcharge — Tier 2",
  paintedColour3: "Painted Colour Surcharge — Tier 3",
};

/** The extra fittings that are cart lines of their own ("Extra fittings" in the price dock). */
const FITTING_LINE_FIELDS = ["inlet2", "overflow2", "outlet2", "highflow1", "highflow2"];
/** Fallback (list) prices of the extra fittings when the sku map has none. */
const FITTING_FALLBACK_PRICES = { inlet2: 33, overflow2: 23, outlet2: 69, highflow1: 231, highflow2: 231 };

/** Pump chip titles (the cart line keeps its full name, addonLineLabel). */
const PUMP_SHORT = {
  mk3Pump: "Evolution MKIV",
  integratedGardenPump: "Integrated Garden Pump System",
  claytechC3X: "Claytech C3X",
  claytechC3X20mmAcqua: "Claytech C3X + Mains Switching",
  claytechC5X: "Claytech C5X",
  claytechC5X20mmAcqua: "Claytech C5X + Mains Switching",
};

/**
 * Pump fit rules the published catalogue doesn't carry yet (it only has
 * addonMinHeightMm { mk3Pump: 800 }): Expivi's live garden pump rule — not on
 * Round tanks, and a tank at least 800 mm tall. The catalogue's own
 * addonMinHeightMm / disabledAddonsByType add to these, never relax them, so
 * publishing the same rule as data changes nothing. The outdoor Claytech
 * pumps have no height rule.
 */
const PUMP_FIT_RULES = {
  integratedGardenPump: { minHeightMm: 800, notOnTypes: ["round"] },
};

/** Theme asset URLs for type/addon thumbnails (from KingspanTankConfig.thumbs). */
function thumbUrl(key) {
  const map = shopifyCfg.thumbs || {};
  return map[key] || "";
}

const TYPE_THUMB_KEYS = {
  Slimline: "slimline",
  Modline: "modline",
  Round: "round",
  Square: "square",
};

const ADDON_THUMB_KEYS = {
  mk3Pump: "mkivPump",
  integratedGardenPump: "gardenPump",
  claytechC3X: "claytechC3X",
  claytechC3X20mmAcqua: "claytechC3XAcqua",
  highflow1: "highflow",
  highflow2: "highflow",
};

/** When true, ATC uses the BYO product variant + line properties (no matrix SKUs).
    Worked out once per sku map (it counts the map's ~16,000 tanks), as the
    size chips ask it for every price they show. */
const baseCartMemo = { map: undefined, value: true };
function useBaseVariantCart() {
  if (baseCartMemo.map !== state.skuMap) {
    baseCartMemo.map = state.skuMap;
    baseCartMemo.value = KingspanTankCore.useBaseVariantCart(state.skuMap);
  }
  return baseCartMemo.value;
}

function resolveTankSku() {
  if (useBaseVariantCart()) return null;
  return KingspanTankCore.resolveTank(state.skuMap, typeKey(), state.values);
}

/**
 * The painted-colour surcharge for the chosen colour: one sku-map addon tier
 * picked by tank capacity (V13 price list p16: KUT-PAINT-1/2/3), or none for
 * a base colour or a size Kingspan won't paint. Becomes a real cart line.
 */
function activePaintTiers() {
  const tier = KingspanTankCore.paintedTier(
    state.data.constraints,
    colourPalette(),
    state.values.tankColour,
    typeKey(),
    state.values
  );
  return tier ? [tier] : [];
}

/** False when Kingspan won't paint the current size (base colours only). */
function sizePaintable() {
  return KingspanTankCore.paintable(typeKey(), state.values);
}

/** What a painted colour adds at the current size (map-gated like the cart). */
function paintedColourPrice() {
  return KingspanTankCore.paintedColourPrice(state.skuMap, typeKey(), state.values);
}

/** Displayed paint surcharge — only what the cart can actually charge, so the
    shown total never exceeds checkout. Unmapped tiers display (and charge) $0. */
function paintSurchargeTotal() {
  return KingspanTankCore.paintSurcharge(state.skuMap, activePaintTiers());
}

/**
 * The name of one add-on cart line — its `Addon` property — and of that line
 * in the price dock's breakdown and the "what changed" note: the sku-map
 * title (the variant's own name, e.g. "Claytech C3X + Mains Switching"),
 * else the builder's label. A paint surcharge names the colour; the Outlet #3
 * fire fitting says so. field: an add-on field, a paint tier key
 * (paintedColour, paintedColour2, paintedColour3), firefit or firefit3.
 */
function addonLineLabel(field) {
  const addons = state.skuMap?.addons || {};
  if (/^paintedColour[23]?$/.test(field)) {
    const title = addons[field]?.title || ADDON_LABELS[field] || ADDON_LABELS.paintedColour;
    const colour = state.values.tankColour;
    return colour ? `${title} — ${colour}` : title;
  }
  if (field === "firefit" || field === "firefit3") {
    const label = state.skuMap?.firefit?.[state.values.region]?.title || ADDON_LABELS.firefit;
    return field === "firefit3" ? `${label} — Outlet #3` : label;
  }
  return addons[field]?.title || ADDON_LABELS[field] || KingspanTankCore.pumpInfo(field)?.title || field;
}

function resolveAddonSkus() {
  const items = [];
  const missing = [];
  const addons = state.skuMap?.addons || {};
  const v = state.values;
  for (const tier of activePaintTiers()) {
    const paint = addons[tier.key];
    if (paint?.variantLegacyId) {
      items.push({ ...paint, field: tier.key, title: addonLineLabel(tier.key) });
    }
  }
  // Fittings, then the pump (PUMPS order): the cart lines follow this order.
  for (const field of TRACKED_ADDON_FIELDS) {
    if (v[field] !== "Yes") continue;
    // Fire fittings are region-priced and resolved below, not from addons{}
    if (field === "firefit" || field === "firefit3") continue;
    const addon = addons[field];
    if (addon?.variantLegacyId) {
      items.push({ field, ...addon, title: addonLineLabel(field) });
      continue;
    }
    // Free / config-only options (e.g. Outlet #3) live in _tank_config
    const price = Number(addon?.price);
    if (addon && (!Number.isFinite(price) || price === 0)) continue;
    missing.push(ADDON_LABELS[field] || field);
  }
  // One fire fitting per outlet that asked for one — outlet #1 and #3 are separate
  // units, so they become separate cart lines (distinct _tank_addon keeps them apart).
  const fire = state.skuMap?.firefit?.[v.region];
  const fireFields = [];
  if (v.firefit && v.firefit !== "No") fireFields.push("firefit");
  if (v.firefit3 === "Yes") fireFields.push("firefit3");
  for (const field of fireFields) {
    if (fire?.variantLegacyId) {
      items.push({ ...fire, field, title: addonLineLabel(field) });
    } else {
      missing.push(`${ADDON_LABELS[field] || ADDON_LABELS.firefit} (${v.region})`);
    }
  }
  return { items, missing };
}

const PRICE_ON_ENQUIRY = "Price on enquiry";

/** True when the tank has no size-priced product (the sku map failed to load,
    or this size has none). Add to cart blocks it, so no price is shown. */
function tankPriceUnavailable() {
  return useBaseVariantCart() || !resolveTankSku()?.variantLegacyId;
}

function baseProductPrice() {
  const tank = resolveTankSku();
  if (tank?.price != null) {
    const n = Number(tank.price);
    if (Number.isFinite(n)) return n;
  }
  const fromMap = state.skuMap?.basePrices?.[typeKey()];
  if (fromMap != null) {
    const n = Number(fromMap);
    if (Number.isFinite(n)) return n;
  }
  const page = lockedTypePage();
  if (page?.basePrice != null) return page.basePrice;
  const n = Number(shopifyCfg.basePrice);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The two builder offers (KingspanTankCore.TANK_PROMO / PROMO_OFFERS): the tank
 * offer (10% off a Slimline/Modline tank) and the pump offer (10% off ONE of
 * the six pumps with any builder tank, never below that pump's list price).
 * Always worked out from the build — type, delivery state, prices and the
 * pump's variant — never carried in links or cart lines. Which offer runs,
 * from when to when and for which states comes from the shop metafield
 * kx_promo.offers (layout/theme.liquid prints it as window.KingspanTankOffers;
 * the KPO operator script writes it from the checkout discounts): missing,
 * malformed, disabled or ended means no offer. SA is never eligible.
 */
function promoPolicy() {
  return KingspanTankCore.promoPolicy();
}

/** What the offers take off qty tanks of this build (default ONE): { tank,
    addons, total }. Only the Slimline/Modline tank (tank offer) and one pump
    at or above its list price (pump offer) qualify; the core decides, so every
    priced add-on is passed and the rest come back 0. The sku map gives each
    pump's variant, which the offer must name. */
function promoParts(qty = 1) {
  if (tankPriceUnavailable()) return { tank: 0, addons: {}, total: 0 };
  const v = state.values;
  const addons = {};
  for (const field of TRACKED_ADDON_FIELDS) {
    if (v[field] === "Yes") addons[field] = addonPrice(field, 0);
  }
  return KingspanTankCore.promoBreakdown(promoPolicy(), {
    typeKey: typeKey(),
    region: v.region,
    tankPrice: baseProductPrice(),
    addons,
    qty,
    map: state.skuMap,
  });
}

/** An offer that ends (or a scheduled one that starts) while the page is open:
    re-price just after that instant (KingspanTankCore.promoNextChangeMs), as
    checkout stops or starts it then. */
function watchPromoEnd() {
  KingspanTankCore.onPromoChange(promoPolicy(), () => {
    renderAll();
    watchPromoEnd();
  });
}

/**
 * One discount line per offer that takes something off, tank first, each
 * naming its own label, what it covers and that offer's states:
 * [{ offer, field, label, item, amount, terms, text }], e.g. text
 * "10% off with your tank — Claytech C3X pump (NSW/ACT, VIC & QLD delivery
 * only)". Amounts are for the qty promoParts() was given. [] when nothing.
 */
function promoOfferLines(p = promoParts()) {
  return KingspanTankCore.promoLabels(promoPolicy(), p);
}

/** For the AI chat: the offers already taken off the total, or "". */
function promoOfferText(lines, p = promoParts()) {
  if (!lines.length) return "";
  const terms = KingspanTankCore.promoTerms(promoPolicy(), p);
  return `${lines.map((l) => `${l.label} — ${l.item} −${money(l.amount)}`).join("; ")} (${
    terms ? `${terms}; ` : ""
  }per tank, applied automatically at checkout)`;
}

/** The build's one pump field, or "" (ensureValidSelections keeps at most one). */
function chosenPump(values = state.values) {
  return PUMP_FIELDS.find((f) => values[f] === "Yes") || "";
}

/** A pump's list price: the sku map's (= the cart line), else the core's list price. */
function pumpPrice(field) {
  return addonPrice(field, KingspanTankCore.pumpInfo(field)?.minUnitPrice ?? 0);
}

/**
 * Per-tank Options / Product / offer and the Total for the quantity — the one
 * source for the price breakdown, getSelection() (the AI chat forwards its
 * total as "Estimated total") and the add-to-cart parity check. The Total
 * takes the offers off each line's subtotal (unit x qty), rounded the way
 * Shopify rounds a line, so it matches the cart at any price and quantity.
 */
function priceSummary() {
  const qty = state.qty || 1;
  const options = optionPriceTotal();
  const product = baseProductPrice();
  const promo = promoParts();
  const promoQty = qty > 1 ? promoParts(qty) : promo;
  const discountQty = promoQty.total;
  const total = (Math.round((options + product) * 100) * qty - Math.round(discountQty * 100)) / 100;
  return { qty, options, product, promo, promoQty, discount: promo.total, discountQty, total };
}

/** "1150×4000×2320mm", "Ø1500×1020mm", "1000×1000×1020mm" — the size the shopper picked, or "". */
function sizeText(v = state.values) {
  const tk = typeKey();
  if (tk === "round") return v.height && v.diameter ? `Ø${v.diameter}×${v.height}mm` : "";
  if (tk === "square") return v.height && v.sWidth ? `${v.sWidth}×${v.sWidth}×${v.height}mm` : "";
  return v.height && v.width && v.length ? `${v.width}×${v.length}×${v.height}mm` : "";
}

/**
 * The build's priced parts PER TANK, in the price dock's order, each a group
 * of the cart lines it charges:
 *   [{ key: 'tank' | 'colour' | 'fittings' | 'firefit' | 'pump', label,
 *      detail, price (dollars), lines: [{ field, label (addonLineLabel),
 *      price }] }]
 * The tank is always first; a group with nothing to charge is left out.
 * optionPriceTotal() is the sum of every group but the tank, so the dock, the
 * breakdown, getSelection() and the cart lines come from one list.
 */
function priceItems() {
  const v = state.values;
  const items = [
    { key: "tank", label: "Tank", detail: [v.type, sizeText()].filter(Boolean).join(" "), price: baseProductPrice(), lines: [] },
  ];
  const paint = paintSurchargeTotal();
  if (paint > 0) {
    const tier = activePaintTiers()[0];
    items.push({
      key: "colour",
      label: "Colour",
      detail: `${v.tankColour} (painted)`,
      price: paint,
      lines: tier ? [{ field: tier.key, label: addonLineLabel(tier.key), price: paint }] : [],
    });
  }
  const fittings = FITTING_LINE_FIELDS.filter((f) => v[f] === "Yes").map((f) => ({
    field: f,
    label: addonLineLabel(f),
    price: addonPrice(f, FITTING_FALLBACK_PRICES[f]),
  }));
  if (fittings.length) {
    items.push({
      key: "fittings",
      label: "Extra fittings",
      detail: fittings.map((l) => l.label).join(", "),
      price: fittings.reduce((s, l) => s + l.price, 0),
      lines: fittings,
    });
  }
  // Outlet #1 and outlet #3 each take their own fire fitting — priced per unit,
  // sku map first (the cart charges that variant), then the catalogue.
  const fireFields = [];
  if (v.firefit && v.firefit !== "No") fireFields.push("firefit");
  if (v.firefit3 === "Yes") fireFields.push("firefit3");
  const fireUnit = fireFields.length ? fireUnitPrice() : NaN;
  if (fireFields.length && Number.isFinite(fireUnit)) {
    items.push({
      key: "firefit",
      label: fireFields.length > 1 ? "Fire fittings" : "Fire fitting",
      detail: `${regionLabel() || v.region}${fireFields.length > 1 ? ` × ${fireFields.length}` : ""}`,
      price: fireUnit * fireFields.length,
      lines: fireFields.map((f) => ({ field: f, label: addonLineLabel(f), price: fireUnit })),
    });
  }
  // Full list price; the pump offer's 10% is a separate discount line.
  const pump = chosenPump();
  if (pump) {
    const price = pumpPrice(pump);
    items.push({ key: "pump", label: "Pump", detail: addonLineLabel(pump), price, lines: [{ field: pump, label: addonLineLabel(pump), price }] });
  }
  return items;
}

/**
 * What the price dock shows, for the quantity: { qty, unpriced, totalCents,
 * totalText, saveCents ("You save"), terms (where ALL of that saving applies:
 * "NSW/ACT, VIC & QLD delivery only"; '' when nothing is saved), rows: the
 * breakdown, each { key, label, detail, cents } — the priceItems() groups for
 * qty tanks, then one row per offer (key 'offer', offer, cents < 0) — whose
 * cents add up to totalCents exactly }. Unpriced (no size price): no rows.
 */
function dockModel(summary = priceSummary()) {
  const { qty, total, promoQty } = summary;
  const unpriced = tankPriceUnavailable();
  const totalCents = Math.round(total * 100);
  const model = {
    qty,
    unpriced,
    totalCents,
    totalText: unpriced ? PRICE_ON_ENQUIRY : money(total),
    saveCents: 0,
    terms: "",
    rows: [],
  };
  if (unpriced) return model;
  model.rows = priceItems().map((g) => ({
    key: g.key,
    label: g.label,
    detail: g.detail,
    cents: Math.round(g.price * 100) * qty,
  }));
  for (const l of promoOfferLines(promoQty)) {
    model.rows.push({ key: "offer", offer: l.offer, field: l.field, label: `${l.label} — ${l.item}`, detail: "", cents: -Math.round(l.amount * 100) });
  }
  model.saveCents = Math.round(promoQty.total * 100);
  model.terms = model.saveCents > 0 ? KingspanTankCore.promoTerms(promoPolicy(), promoQty) : "";
  return model;
}

function applyTypeChrome(page, { forceTitle = false } = {}) {
  if (!page) return;
  const titleEls = [
    document.getElementById("productTitle"),
    document.querySelector(".product__below-gallery-title"),
    document.querySelector(".breadcrumbs span:last-child"),
  ];
  titleEls.forEach((el) => {
    if (!el) return;
    if (forceTitle || !shopifyCfg.productTitle || !state.lockedType) {
      el.textContent = page.title;
    } else {
      el.textContent = shopifyCfg.productTitle;
    }
  });
  const desc = document.getElementById("typeDescription");
  if (desc) desc.innerHTML = page.descriptionHtml;
  // Visible description under the 3D viewer on the Square-style AI layout
  const visibleDesc = document.querySelector("[data-kut-product-info] .rte");
  if (visibleDesc && !state.lockedType) {
    visibleDesc.innerHTML = `<h3>${escapeHtml(page.title)}</h3>${page.descriptionHtml}`;
  }
  const sizeLine = document.getElementById("typeSizeSummary");
  if (sizeLine) sizeLine.textContent = page.sizeSummary;
  document.body.dataset.tankType = page.key;
  // Family-tagged product sections for the other types are now hidden (configurator CSS): a video
  // playing in one would carry on out of sight, so pause it (the sections' pause handlers reset the overlay).
  document.querySelectorAll("[data-kx-family]").forEach((section) => {
    if (section.getAttribute("data-kx-family").split(/\s+/).includes(page.key)) return;
    section.querySelectorAll("video").forEach((video) => {
      if (!video.paused) video.pause();
    });
  });
  const root = document.getElementById("kut-root");
  if (root) root.dataset.tankType = page.key;
  const info = document.querySelector("[data-kut-product-info]");
  if (info) info.dataset.tankType = page.key;
  const tank = document.getElementById("tankVisual");
  if (tank) {
    tank.classList.remove("slimline", "modline", "round", "square");
    tank.classList.add(page.key);
  }
}

function applyLockedType() {
  const page = lockedTypePage();
  if (!page) return;
  state.values.type = page.name;
  syncTypeKey();
  applyTypeChrome(page, { forceTitle: false });
}

function applySelectedTypeChrome() {
  if (state.lockedType) return;
  const page = TYPE_PAGES[typeKey()];
  if (page) applyTypeChrome(page, { forceTitle: true });
}

/**
 * The dimensions each shape actually has. The state object keeps a slot for every shape, so
 * emitting all of them puts dimensions on the order the shopper never chose — a square tank
 * arriving with a diameter and a length, for instance.
 */
const DIMENSION_PROPS = {
  round: { Diameter: "diameter" },
  square: { Width: "sWidth" },
  slimline: { Width: "width", Length: "length" },
  modline: { Width: "width", Length: "length" },
};

/** Cart-line position property for a fire fitting addon line (else nothing). */
function firePositionProp(field) {
  if (field === "firefit") return { Position: state.values.firefitPos || "Not sure" };
  if (field === "firefit3") return { Position: state.values.firefit3Pos || "Not sure" };
  return {};
}

function buildCartProps(groupId, tank) {
  const shape = DIMENSION_PROPS[typeKey()] || {};
  const keep = Object.values(shape);
  const config = { ...state.values };
  ["width", "sWidth", "length", "diameter"].forEach((key) => {
    if (!keep.includes(key)) delete config[key];
  });

  const props = {
    _tank_config: JSON.stringify(config),
    _kut_group: groupId,
    _kut_edit: "1",
    Region: state.values.region,
    Type: state.values.type,
  };
  // Dimensions listed width → length → height to match the configurator steps.
  Object.keys(shape).forEach((label) => {
    const value = state.values[shape[label]];
    if (value) props[label] = `${value} mm`;
  });
  if (state.values.height) props.Height = `${state.values.height} mm`;
  // How big the tank is, the figure the page's usage stats show (cart, checkout, the order).
  const litres = KingspanTankCore.computeCapacity(typeKey(), state.values);
  if (litres > 0) props.Capacity = `${Math.round(Number(litres)).toLocaleString("en-AU")} L`;
  if (state.values.tankColour) props.Colour = state.values.tankColour;
  if (state.values.firefit && state.values.firefit !== "No") {
    props["Fire fitting position"] = state.values.firefitPos || "Not sure";
  }
  if (state.values.outlet3 === "Yes" && state.values.firefit3 === "Yes") {
    props["Fire fitting (Outlet #3) position"] = state.values.firefit3Pos || "Not sure";
  }
  if (tank?.sku) props.SKU = tank.sku;
  // No offer properties: the KPO checkout discount works the offer out from
  // Region, _kut_group and the SKU/variant, so nothing here can claim one.
  return props;
}

/**
 * One add-on line of a build: same quantity and _kut_group as its tank, named
 * by addonLineLabel (the Addon property). Region/Type/Colour stay on every
 * line (the chat and the KPO function read them; the cart hides them on
 * add-on rows). With CART_NESTING on, parent_id nests it under the tank line
 * (the tank's variant id, in the same /cart/add.js request); off, the cart
 * groups the flat lines by _kut_group.
 */
function addonCartItem(addon, groupId, qty, parentVariantId, { nested = CART_NESTING.enabled } = {}) {
  const item = {
    id: Number(addon.variantLegacyId),
    quantity: qty,
    properties: {
      _tank_addon: addon.field,
      _kut_group: groupId,
      Addon: addon.title || addonLineLabel(addon.field),
      Region: state.values.region,
      Type: state.values.type,
      Colour: state.values.tankColour,
      ...firePositionProp(addon.field),
    },
  };
  if (nested && parentVariantId) item.parent_id = Number(parentVariantId);
  return item;
}

/** BYO product variant only (plus any resolved staging addons). */
function buildCartItemsBaseOnly(opts = {}) {
  const qty = state.qty || 1;
  const groupId = newConfigGroupId();
  const props = {
    ...buildCartProps(groupId, null),
    "Options total": money(optionPriceTotal()),
    _fallback_base: "true",
  };
  const items = [];
  if (shopifyCfg.variantId) {
    items.push({
      id: Number(shopifyCfg.variantId),
      quantity: qty,
      properties: props,
    });
  }
  const parent = items[0]?.id || null;
  const { items: addonItems } = resolveAddonSkus();
  for (const addon of addonItems) items.push(addonCartItem(addon, groupId, qty, parent, opts));
  return items;
}

/**
 * The /cart/add.js items for this build: the tank line first, then its
 * add-on lines (paint, fittings, the pump in PUMPS order, fire fittings).
 * opts.nested overrides CART_NESTING.enabled (tests, the staging probe).
 */
function buildCartItems(opts = {}) {
  if (useBaseVariantCart()) return buildCartItemsBaseOnly(opts);

  const tank = resolveTankSku();
  const qty = state.qty || 1;
  const groupId = newConfigGroupId();
  const props = buildCartProps(groupId, tank);

  const items = [];
  if (tank?.variantLegacyId) {
    items.push({
      id: Number(tank.variantLegacyId),
      quantity: qty,
      properties: props,
    });
  } else if (shopifyCfg.variantId) {
    items.push({
      id: Number(shopifyCfg.variantId),
      quantity: qty,
      properties: {
        ...props,
        "Options total": money(optionPriceTotal()),
        _fallback_base: "true",
      },
    });
  }

  const parent = items[0]?.id || null;
  const { items: addonItems } = resolveAddonSkus();
  for (const addon of addonItems) items.push(addonCartItem(addon, groupId, qty, parent, opts));
  return items;
}

/** Fields accepted in ?kut=… JSON or as individual query params */
const DEEP_LINK_KEYS = [
  "region",
  "type",
  "typeKey",
  "height",
  "width",
  "length",
  "diameter",
  "sWidth",
  "tankColour",
  "Base_colours",
  "paintedColor1",
  "chooseFittingsNow",
  "inlet",
  "inlet2",
  "inletPos",
  "inlet2Pos",
  "overflow",
  "overflow2",
  "overflowPos",
  "overflow2Pos",
  "outlet",
  "outlet2",
  "outlet3",
  "outletPos",
  "outlet2Pos",
  "outlet3Pos",
  "firefit",
  "firefit3",
  "firefitPos",
  "firefit3Pos",
  "highflow1",
  "highflow2",
  ...PUMP_FIELDS,
  "pumpPos",
];

const DEEP_LINK_ALIASES = {
  colour: "tankColour",
  color: "tankColour",
  tank_colour: "tankColour",
  tank_color: "tankColour",
  fittings: "chooseFittingsNow",
  choose_fittings: "chooseFittingsNow",
};

function encodeKutPayload(config) {
  const json = JSON.stringify(config);
  const b64 =
    typeof btoa === "function"
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, "utf8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function decodeKutPayload(raw) {
  if (!raw) return null;
  try {
    const b64 = String(raw)
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(String(raw).length / 4) * 4, "=");
    const json =
      typeof atob === "function"
        ? decodeURIComponent(escape(atob(b64)))
        : Buffer.from(b64, "base64").toString("utf8");
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    try {
      const parsed = JSON.parse(decodeURIComponent(String(raw)));
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
}

function pickDeepLinkConfig(source) {
  if (!source || typeof source !== "object") return {};
  const out = {};
  for (const key of DEEP_LINK_KEYS) {
    if (source[key] != null && source[key] !== "") out[key] = String(source[key]);
  }
  return out;
}

function openAccordionsForConfig(config) {
  if (PUMP_FIELDS.some((f) => config[f] === "Yes")) state.accordions.pump = true;
  // Highflow and fire fittings render with their overflow/outlet inside the
  // fittings accordion, so those deep links open Fittings.
  if (
    ["inlet2", "overflow2", "outlet2", "outlet3", "highflow1", "highflow2", "firefit3"].some(
      (k) => config[k] === "Yes"
    ) ||
    (config.firefit && config.firefit !== "No") ||
    config.chooseFittingsNow === "Yes"
  ) {
    state.accordions.fittings = true;
  }
}

/**
 * Prefill from deep link:
 * - Readable params: ?region=VIC&height=1020&width=600&length=1300&mk3Pump=Yes
 * - Compact JSON:    ?kut=<base64url JSON of selection object>
 * Individual params override keys inside ?kut=
 */
function loadConfigFromUrl() {
  const params = new URLSearchParams(location.search);
  if (params.get("kut_edit")) return false;

  let config = {};
  const compact = params.get("kut") || params.get("config");
  if (compact) {
    const parsed = decodeKutPayload(compact);
    if (parsed) config = { ...pickDeepLinkConfig(parsed), ...config };
  }

  for (const [rawKey, value] of params.entries()) {
    if (
      rawKey === "kut" ||
      rawKey === "config" ||
      rawKey === "kut_edit" ||
      rawKey === "qty" ||
      rawKey === "preset" ||
      // Old homepage-card offer links: ignored — the offer is worked out from the build
      rawKey === "discount" ||
      rawKey === "discountLabel" ||
      rawKey === "code"
    ) {
      continue;
    }
    // Locked product pages ignore ?type= for the product itself, but allow other fields
    if (rawKey === "type" && state.lockedType) continue;
    const field = DEEP_LINK_ALIASES[rawKey] || rawKey;
    if (DEEP_LINK_KEYS.includes(field)) {
      config[field] = value;
    }
  }

  const qty = params.get("qty");
  if (qty != null && qty !== "") {
    state.qty = Math.max(1, Math.floor(Number(qty)) || 1); // whole tanks only
    const qtyInput = document.getElementById("qtyInput");
    if (qtyInput) qtyInput.value = String(state.qty);
  }

  config = pickDeepLinkConfig(config);
  if (!Object.keys(config).length) return false;

  // Region aliases: NSW/ACT, nsw / act, "Sa " → the catalogue value. Spacing and
  // case don't matter, in one pass, the same as the featured cards' regionOf().
  if (config.region) {
    const regions = (state.data?.options?.region || []).map((o) => o.name);
    const squash = (s) => String(s).replace(/\s+/g, "").toLowerCase();
    const want = squash(config.region);
    const match = regions.find((r) => r === config.region) || (want && regions.find((r) => squash(r) === want));
    if (match) config.region = match;
  }
  // Type aliases: round / ROUND → Round (as the hub reads ?type= at boot); the
  // featured cards read links the same way.
  if (config.type) {
    const key = resolveLockedTypeKey(config.type);
    if (key) config.type = TYPE_PAGES[key].name;
  }

  state.values = { ...state.values, ...config };
  openAccordionsForConfig(config);

  // Curated preset link: collapse the form behind a summary card. The param
  // value doubles as the display name (?preset=The%20Courtyard); "1"/"yes"
  // enable the mode without a name.
  const presetParam = params.get("preset");
  if (presetParam != null && presetParam !== "") {
    const generic = presetParam === "1" || presetParam.toLowerCase() === "yes";
    const dims = {};
    for (const k of PRESET_SIZE_FIELDS) if (config[k] != null && config[k] !== "") dims[k] = String(config[k]);
    // The pump the preset names (the first the link asks for, in PUMPS order:
    // the one ensureValidSelections keeps), and the fire fitting. Read loosely
    // ("yes", "Yes please"), so that guardPresetSize() can check the builder
    // really kept it: the builder only counts a pump set to exactly "Yes".
    const askedPump = PUMP_FIELDS.find((k) => YES_VALUE.test(String(config[k] || "")));
    const addons = PRESET_ADDON_FIELDS.filter(
      (k) => YES_VALUE.test(String(config[k] || "")) && (!PUMP_FIELDS.includes(k) || k === askedPump)
    );
    state.preset = { name: generic ? "" : presetParam, editing: false, asked: { type: config.type || null, dims, addons } };
  }
  return true;
}

const PRESET_SIZE_FIELDS = ["width", "length", "height", "diameter", "sWidth"];
/**
 * Add-ons a preset can name, asked for as "Yes": any of the six pumps ("… with
 * Evolution MKIV pump"; one per tank, the first in PUMPS order) and the fire
 * fitting (fire tanks).
 */
const PRESET_ADDON_FIELDS = [...PUMP_FIELDS, "firefit"];
const YES_VALUE = /^\s*yes\b/i;

/**
 * A ?preset= link names one tank. When the size it asks for isn't sold (any
 * more), ensureValidSelections() snaps to another size — or, in a state that
 * doesn't sell the type, to another type — and the preset's name and summary
 * would then describe a tank the shopper isn't getting (a retired 3550 mm
 * round opened as a 1200 mm round under its "22900 Litre" name). The same
 * goes for a link that names only part of its type's size (a Square asked for
 * by width instead of sWidth opens the default width), and for a pump or fire
 * fitting it names that the tank can't have (dropped to "No": the MKIV and the
 * garden pump on a tank under 800 mm, the garden pump on a Round, a pump the
 * catalogue switches off for the type, a fire fitting the state doesn't sell).
 * Drop the preset and say the tank is gone; the form shows what is selected instead.
 */
function guardPresetSize() {
  const asked = state.preset?.asked;
  if (!asked) return false;
  const v = state.values;
  const shown = visibleDimFields();
  const typeMoved = !!asked.type && asked.type !== v.type;
  const sizeMoved = Object.entries(asked.dims).some(([k, want]) => shown.includes(k) && String(v[k]) !== want);
  // A link that names a size names all of it (a type-only preset names none).
  const sizePartial = Object.keys(asked.dims).length > 0 && shown.some((k) => !(k in asked.dims));
  // A pump counts only as exactly "Yes" (chosenPump(), and the cards' v[f] === 'Yes'), so a
  // link with mk3Pump=yes, or with mk3Pump=yes and claytechC3X=Yes, can't keep a preset named
  // for a pump the build doesn't carry; the fire fitting is normalised to "Yes…" by normaliseFirefit.
  const addonMoved = (asked.addons || []).some((k) =>
    PUMP_FIELDS.includes(k) ? v[k] !== "Yes" : !YES_VALUE.test(String(v[k] || ""))
  );
  if (!typeMoved && !sizeMoved && !sizePartial && !addonMoved) return false;
  state.preset = null;
  let note = document.getElementById("kutPresetGone");
  if (!note) {
    note = document.createElement("p");
    note.id = "kutPresetGone";
    note.className = "kut-edit-banner";
    note.setAttribute("role", "status");
    const root = document.getElementById("kut-root");
    root?.insertBefore(note, root.firstChild);
  }
  note.textContent =
    typeMoved || sizeMoved || sizePartial
      ? "This size is no longer available. Choose a size below to build your tank."
      : "The pump or fire fitting in this link isn't available for this tank, size or state. Check the options below to build your tank.";
  return true;
}

/**
 * Build a deep link for the current (or provided) configuration.
 *  - By default: this page's own address carrying every field (?kut= base64
 *    JSON, or readable params with compact: false), absolute unless
 *    absolute: false.
 *  - { share: true }: the short readable link the featured cards and the
 *    range-page presets use (shareLink): the hub builder's path (with the
 *    origin only when absolute: true), and only the fields this build needs.
 *    region: false leaves the delivery state to the shopper; preset: "<name>"
 *    adds &preset=<name>. The staff "Copy link to this build" button copies it.
 */
function buildDeepLink(values = state.values, opts = {}) {
  if (opts && opts.share) return shareLink(values, opts);
  const { compact = true, absolute = true } = opts || {};
  const config = pickDeepLinkConfig(values);
  const url = new URL(absolute ? location.href : location.pathname + location.search, location.origin);
  // Drop prior deep-link noise (incl. old offer params and retired fields)
  [
    "kut", "config", "kut_edit", "qty", "preset", "discount", "discountLabel", "code",
    "paintedColor2", "paintedColor3", ...RETIRED_ADDON_FIELDS,
    ...DEEP_LINK_KEYS, ...Object.keys(DEEP_LINK_ALIASES),
  ].forEach((k) => url.searchParams.delete(k));
  if (compact) {
    url.searchParams.set("kut", encodeKutPayload(config));
  } else {
    Object.entries(config).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  if (state.qty > 1) url.searchParams.set("qty", String(state.qty));
  return url.toString();
}

/**
 * Every shared link opens the hub builder, whichever product page the build
 * was made on: the featured cards and the range-page presets link there too
 * (scripts/golive/build-tank-presets.mjs BUILDER), and it is the page that
 * takes ?type= (a locked type page ignores it).
 */
const SHARE_LINK_PATH = "/products/build-your-own-tank";

/** The extra and fire fittings, each with its position field. */
const SHARE_FITTING_POSITIONS = [
  ["inlet2", "inlet2Pos"],
  ["overflow2", "overflow2Pos"],
  ["outlet2", "outlet2Pos"],
  ["outlet3", "outlet3Pos"],
  ["firefit", "firefitPos"],
  ["firefit3", "firefit3Pos"],
];

/** A fitting or pump is on ("Yes"; the fire fitting is its state's "Yes +$…" option). */
function fieldOn(v, field) {
  return field === "firefit" ? !!v.firefit && v.firefit !== "No" : v[field] === "Yes";
}

/**
 * The order a shared link names its fields in: the presets' order (type, the
 * size, state, pump, fire fitting, chooseFittingsNow: build-tank-presets.mjs
 * deepLink()), with the colour after the state and each fitting next to its
 * position.
 */
function shareLinkOrder(v) {
  return [
    "type", ...visibleDimFields(v), "region", "tankColour",
    ...PUMP_FIELDS, "pumpPos",
    "inletPos", "inlet2", "inlet2Pos",
    "overflowPos", "overflow2", "overflow2Pos", "highflow1", "highflow2",
    "outletPos", "outlet2", "outlet2Pos", "outlet3", "outlet3Pos",
    "firefit", "firefitPos", "firefit3", "firefit3Pos",
    "chooseFittingsNow",
  ];
}

/**
 * What makes a build what it is (its cart lines and their properties, the
 * summary card, the 3D view): type, size, state, colour, the fittings choice,
 * the one pump and every extra and fire fitting; the MKIV's position (the one
 * pump that has one); and, while the fittings are chosen now, the positions
 * of Inlet, Overflow and Outlet #1 and of each extra or fire fitting that is on.
 */
function shareLinkFields(v) {
  const out = ["type", ...visibleDimFields(v), "region", "tankColour", "chooseFittingsNow", ...PUMP_FIELDS, ...OPTIONAL_FITTING_FIELDS];
  if (v.mk3Pump === "Yes") out.push("pumpPos");
  if (v.chooseFittingsNow !== "No") {
    out.push("inletPos", "overflowPos", "outletPos");
    for (const [field, pos] of SHARE_FITTING_POSITIONS) if (fieldOn(v, field)) out.push(pos);
  }
  return out;
}

/**
 * What the hub builder ends up with when it opens a link naming `config`:
 * boot() (?type= preselects the type, the boot values settle), then
 * loadConfigFromUrl() (the link's fields over them, settled again). It runs on
 * scratch values and puts the page's own state back as it was.
 */
function settleLink(config) {
  const saved = {
    values: state.values,
    lockedType: state.lockedType,
    paintedWish: state.paintedWish,
    paintNotice: state.paintNotice,
    noticeField: state.noticeField,
  };
  try {
    state.lockedType = null;
    state.paintedWish = null;
    state.paintNotice = null;
    state.noticeField = null;
    state.values = bootValues();
    const key = resolveLockedTypeKey(config.type);
    if (key) state.values.type = TYPE_PAGES[key].name;
    syncColourFromSelection();
    syncTypeKey();
    ensureValidSelections();
    state.values = { ...state.values, ...pickDeepLinkConfig(config) };
    syncColourFromSelection();
    syncTypeKey();
    ensureValidSelections();
    return { ...state.values };
  } finally {
    Object.assign(state, saved);
  }
}

/**
 * The fewest fields that rebuild `v` on the hub builder, in link order:
 *  - always the type and all of its size (a preset link names its whole size:
 *    guardPresetSize), the state unless region: false, and chooseFittingsNow
 *    when an extra or fire fitting is on (as the fire presets have it);
 *  - then, one at a time, each field the builder would otherwise open
 *    differently (settleLink): a colour, the pump, a fitting, a moved position.
 * A fire fitting is named "Yes" (each state has its own kit; the builder picks
 * it). With region: false the build is still compared in v's state: a shopper
 * in another state gets that state's colours, kit and prices.
 * unmatched: fields no link brings back (none expected; the staff box warns).
 */
function shareLinkConfig(v = state.values, { region = true } = {}) {
  const order = shareLinkOrder(v);
  const fields = shareLinkFields(v);
  const value = (f) => (f === "firefit" ? (fieldOn(v, f) ? "Yes" : "No") : String(v[f] ?? ""));
  const config = { type: value("type") };
  for (const f of visibleDimFields(v)) config[f] = value(f);
  if (region) config.region = value("region");
  if (v.chooseFittingsNow !== "No" && OPTIONAL_FITTING_FIELDS.some((f) => fieldOn(v, f))) {
    config.chooseFittingsNow = value("chooseFittingsNow");
  }
  let unmatched = [];
  for (let i = 0; i <= order.length; i++) {
    const got = settleLink({ ...config, region: v.region });
    unmatched = fields.filter((f) => String(got[f] ?? "") !== String(v[f] ?? ""));
    const next = order.find((f) => unmatched.includes(f) && !(f in config));
    if (!next) break;
    config[next] = value(next);
  }
  return { entries: order.filter((f) => f in config).map((f) => [f, config[f]]), unmatched };
}

/** The short readable link and the fields it can't bring back (buildDeepLink { share: true }). */
function shareLinkParts(values = state.values, { region = true, preset = "", absolute = false } = {}) {
  const { entries, unmatched } = shareLinkConfig(values, { region });
  const q = entries.map(([k, val]) => `${k}=${encodeURIComponent(val)}`);
  if (state.qty > 1) q.push(`qty=${state.qty}`);
  const name = String(preset ?? "").trim();
  if (name) q.push(`preset=${encodeURIComponent(name)}`);
  return { link: `${absolute ? location.origin : ""}${SHARE_LINK_PATH}?${q.join("&")}`, unmatched };
}

function shareLink(values = state.values, opts = {}) {
  return shareLinkParts(values, opts).link;
}

window.KingspanTankDeepLink = {
  encode: encodeKutPayload,
  decode: decodeKutPayload,
  build: buildDeepLink,
  current: () => buildDeepLink(),
};

/* ---- Staff: "Copy link to this build" ------------------------------------
 * The link that opens the build on screen (buildDeepLink { share: true }), to
 * paste into a featured card's or a preset's "Builder deep link" in the theme
 * editor (sections/kx-featured-tanks.liquid names this button). */

/** The button's label: the theme editor's help text names it. */
const STAFF_LINK_LABEL = "Copy link to this build";

/**
 * Staff only: inside the theme editor (request.design_mode, passed as
 * KingspanTankConfig.designMode), or with ?kx_staff=1 in the address. A
 * shopper who types the flag only gets a box that copies a link to their own
 * build; the builder, its prices and the cart are the same either way.
 */
function staffLinkOn() {
  if (shopifyCfg.designMode === true) return true;
  try {
    return new URLSearchParams(location.search).get("kx_staff") === "1";
  } catch (_) {
    return false;
  }
}

/** The link the status line is about (it clears when the link changes). */
let staffStatusLink = null;

function initStaffLink() {
  if (!staffLinkOn() || document.getElementById("kutStaffLink")) return;
  const root = document.getElementById("kut-root");
  if (!root) return;
  const box = document.createElement("section");
  box.className = "kut-staff";
  box.id = "kutStaffLink";
  box.setAttribute("aria-labelledby", "kutStaffTitle");
  box.innerHTML = `
    <p class="kut-staff__title" id="kutStaffTitle">Staff: link to this build</p>
    <p class="kut-staff__intro">Copies the link that opens this build, for a featured card or a preset in the theme editor. Shoppers don't see this box: only the theme editor and <code class="kut-staff__code">?kx_staff=1</code> show it.</p>
    <label class="kut-staff__label" for="kutStaffPreset">Preset name (optional: the card's title)</label>
    <input class="kut-staff__input" id="kutStaffPreset" type="text" maxlength="80" autocomplete="off" spellcheck="false">
    <label class="kut-staff__check"><input type="checkbox" id="kutStaffRegion" checked> <span id="kutStaffRegionLabel"></span></label>
    <p class="kut-staff__hint" id="kutStaffRegionHint" hidden></p>
    <label class="kut-staff__label" for="kutStaffUrl">Link</label>
    <input class="kut-staff__input kut-staff__url" id="kutStaffUrl" type="text" readonly>
    <p class="kut-staff__warn" id="kutStaffWarn" role="alert" hidden></p>
    <div class="kut-staff__actions">
      <button type="button" class="kut-staff__copy" id="kutStaffCopy">${STAFF_LINK_LABEL}</button>
      <a class="kut-staff__open" id="kutStaffOpen" href="${SHARE_LINK_PATH}" target="_blank" rel="noopener">Open it in a new tab</a>
    </div>
    <p class="kut-staff__status" id="kutStaffStatus" role="status" aria-live="polite"></p>`;
  const purchase = root.querySelector(".kut__purchase");
  if (purchase && purchase.parentNode === root) root.insertBefore(box, purchase.nextSibling);
  else root.appendChild(box);

  // Opened from a preset link: keep its name, so copying it back gives the same link.
  const preset = document.getElementById("kutStaffPreset");
  if (preset && state.preset?.name) preset.value = state.preset.name;
  preset?.addEventListener("input", renderStaffLink);
  document.getElementById("kutStaffRegion")?.addEventListener("change", renderStaffLink);
  document.getElementById("kutStaffCopy")?.addEventListener("click", copyStaffLink);
  const url = document.getElementById("kutStaffUrl");
  url?.addEventListener("focus", () => {
    if (typeof url.select === "function") url.select();
  });
  renderStaffLink();
}

function renderStaffLink() {
  const box = document.getElementById("kutStaffLink");
  if (!box || !state.data) return;
  const keepRegion = !!document.getElementById("kutStaffRegion")?.checked;
  const preset = document.getElementById("kutStaffPreset")?.value || "";
  const { link, unmatched } = shareLinkParts(state.values, { region: keepRegion, preset });

  const regionName = (name) => state.data.constraints.regionDisplay?.[name] || name;
  const regionEl = document.getElementById("kutStaffRegionLabel");
  if (regionEl) regionEl.textContent = `Keep the delivery state (${regionName(state.values.region)}) in the link`;
  const hint = document.getElementById("kutStaffRegionHint");
  if (hint) {
    hint.hidden = keepRegion;
    hint.textContent = keepRegion
      ? ""
      : `The link follows the shopper's delivery state: a card prices it for ${regionName(state.data.defaults.region)}, and a shopper in another state gets that state's colours and fire fitting.`;
  }
  const url = document.getElementById("kutStaffUrl");
  if (url) url.value = link;
  document.getElementById("kutStaffOpen")?.setAttribute("href", link);
  const warn = document.getElementById("kutStaffWarn");
  if (warn) {
    warn.hidden = !unmatched.length;
    warn.textContent = unmatched.length
      ? `This link opens with a different ${unmatched.join(", ")}: check it with "Open it in a new tab".`
      : "";
  }
  const status = document.getElementById("kutStaffStatus");
  if (status && staffStatusLink !== null && staffStatusLink !== link) {
    status.textContent = "";
    staffStatusLink = null;
  }
}

async function copyStaffLink() {
  renderStaffLink();
  const url = document.getElementById("kutStaffUrl");
  const status = document.getElementById("kutStaffStatus");
  if (!url) return;
  const link = url.value;
  let copied = false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(link);
      copied = true;
    }
  } catch (_) {
    copied = false;
  }
  if (!copied) {
    // The theme editor's frame can block the clipboard API: select the link and try the copy command.
    try {
      url.focus();
      if (typeof url.select === "function") url.select();
      copied = typeof document.execCommand === "function" && document.execCommand("copy") === true;
    } catch (_) {
      copied = false;
    }
  }
  staffStatusLink = link;
  if (status) {
    status.textContent = copied
      ? "Copied. Paste it into the card's “Builder deep link” in the theme editor."
      : "Couldn't copy it here: the link above is selected, press Ctrl+C (⌘C on a Mac).";
  }
}

async function loadEditFromCart() {
  const params = new URLSearchParams(location.search);
  const editKey = params.get("kut_edit");
  if (!editKey) return false;

  try {
    const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => {
      if (!r.ok) throw new Error("Could not load cart");
      return r.json();
    });
    const line = (cart.items || []).find((item) => item.key === editKey);
    if (!line) return false;

    const raw = line.properties?._tank_config;
    if (!raw) return false;

    let config = raw;
    if (typeof raw === "string") {
      try {
        config = JSON.parse(raw);
      } catch (_) {
        return false;
      }
    }
    if (!config || typeof config !== "object") return false;

    // Only fields this builder still has: an old line can carry add-ons it no
    // longer sells (RETIRED_ADDON_FIELDS, the Sensit monitor, ...) or old
    // paint-tier fields, and anything copied here is written back into the
    // replacement's _tank_config. (Old _tank_discount* properties are ignored
    // too — the offer is worked out from the build.)
    const known = new Set([...Object.keys(state.values), ...DEEP_LINK_KEYS]);
    for (const f of [...RETIRED_ADDON_FIELDS, "paintedColor2", "paintedColor3"]) known.delete(f);
    for (const [k, val] of Object.entries(config)) {
      if (known.has(k)) state.values[k] = val;
    }
    state.qty = Math.max(1, Number(line.quantity) || 1);
    state.edit = {
      groupId: line.properties?._kut_group || null,
      lineKey: line.key,
    };

    const qtyInput = document.getElementById("qtyInput");
    if (qtyInput) qtyInput.value = String(state.qty);

    const btn = document.getElementById("btnAddToCart");
    if (btn) {
      const span = btn.querySelector("span");
      if (span) span.textContent = "Update cart";
      else btn.textContent = "Update cart";
    }

    let banner = document.getElementById("kutEditBanner");
    if (!banner) {
      banner = document.createElement("p");
      banner.id = "kutEditBanner";
      banner.className = "kut-edit-banner";
      const root = document.getElementById("kut-root");
      root?.insertBefore(banner, root.firstChild);
    }
    banner.textContent = "Editing a cart item — save to replace it with your new configuration.";
    openAccordionsForConfig(state.values);
    return true;
  } catch (err) {
    console.warn("kut edit load failed", err);
    return false;
  }
}

/**
 * The cart keys that remove a whole build in one /cart/update.js: every line
 * of the group (or the edited line itself), except a nested add-on whose
 * tank goes too — Shopify removes a nested child with its parent, and its key
 * would no longer exist. Flat (un-nested) add-ons are listed themselves.
 */
function buildRemovalKeys(items, groupId, lineKey) {
  const doomed = (items || []).filter(
    (it) => (groupId && it.properties?._kut_group === groupId) || it.key === lineKey
  );
  const doomedKeys = new Set(doomed.map((it) => it.key));
  return doomed
    .filter((it) => !(it.parent_relationship && doomedKeys.has(it.parent_relationship.parent_key)))
    .map((it) => it.key);
}

/** Edit from cart: the edited build goes as a whole — tank and every add-on (nested or flat). */
async function removeEditGroupFromCart() {
  if (!state.edit) return;
  const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => {
    if (!r.ok) throw new Error("Could not load cart");
    return r.json();
  });

  const updates = {};
  for (const key of buildRemovalKeys(cart.items, state.edit.groupId, state.edit.lineKey)) updates[key] = 0;

  if (Object.keys(updates).length === 0) return;

  const res = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function boot() {
  const cfg = window.KingspanTankConfig || {};
  // Only a Liquid/product tankType locks the picker. URL ?type= preselects on the hub builder.
  const urlType =
    typeof location !== "undefined"
      ? new URLSearchParams(location.search).get("type")
      : null;
  state.lockedType = resolveLockedTypeKey(cfg.tankType);

  const fetches = [
    fetch(cfg.catalogueUrl || "./data/catalogue.json"),
    fetch(cfg.sizeRulesUrl || "./data/size_rules.json"),
  ];
  if (cfg.skuMapUrl) fetches.push(fetch(cfg.skuMapUrl).catch(() => null));
  else fetches.push(fetch("./data/tank-sku-map.json").catch(() => null));

  const [catRes, sizeRes, skuRes] = await Promise.all(fetches);
  if (!catRes.ok) throw new Error(`Catalogue request failed (${catRes.status})`);
  if (!sizeRes.ok) throw new Error(`Size rules request failed (${sizeRes.status})`);
  state.data = await catRes.json();
  state.sizeRules = await sizeRes.json();
  if (skuRes && skuRes.ok) {
    try {
      state.skuMap = await skuRes.json();
    } catch (_) {
      state.skuMap = null;
    }
  }
  state.values = bootValues();
  // Hub builder: ?type=slimline|modline|round|square preselects without locking the picker
  if (!state.lockedType && urlType) {
    const key = resolveLockedTypeKey(urlType);
    const page = key ? TYPE_PAGES[key] : null;
    if (page) state.values.type = page.name;
  }
  applyLockedType();
  syncColourFromSelection();
  syncTypeKey();
  ensureValidSelections();
  applyLockedType();
  const editing = await loadEditFromCart();
  if (!editing) {
    // A size page starts at its own size; a deep link's fields go over it (one naming no size keeps it).
    applyPageVariantSize();
    loadConfigFromUrl();
  }
  applyLockedType();
  syncColourFromSelection();
  syncTypeKey();
  ensureValidSelections();
  guardPresetSize();
  applySelectedTypeChrome();
  renderAll();
  bindPurchase();
  bindDeliveryCheck();
  bindDock();
  bindInfoTips();
  bindKxFocus();
  initKxViewer();
  initStaffLink();
  watchPromoEnd();
  // Re-apply edit button labels after bind (form re-renders don't wipe them)
  if (state.edit) setAtcLabels("Update cart");
}

/**
 * A size product's page (type locked, e.g. /products/urban-slimline-tank-700mm?variant=...) opens the
 * builder at the page's own size: its selected variant (Shopify's selected_or_first_available_variant, so
 * ?variant= or the series' first size), found in the SKU map by variant id. Without it the builder opened
 * at the catalogue default (600 mm) under a "700 mm Series" title, and the cart line named another series.
 * An edit from the cart skips it; a deep link's fields go over it.
 */
function applyPageVariantSize() {
  const tanks = state.skuMap && state.skuMap.tanks;
  if (!state.lockedType || !shopifyCfg.variantId || !tanks) return false;
  const id = Number(shopifyCfg.variantId);
  const key = Object.keys(tanks).find((k) => Number(tanks[k] && tanks[k].variantLegacyId) === id);
  if (!key) return false;
  const [type, height, a, b] = key.split("|");
  if (type !== state.lockedType) return false;
  const dims = type === "round" ? { height, diameter: a } : type === "square" ? { height, sWidth: a } : { height, width: a, length: b };
  state.values = { ...state.values, ...dims };
  return true;
}

/**
 * The values every page load starts from, before ?type=, a deep link or an
 * edit from the cart (boot), and before ensureValidSelections() snaps the
 * size and positions to the type. settleLink() starts from them too.
 */
function bootValues() {
  return {
    ...state.data.defaults,
    tankColour: state.data.defaults.tankColour || "Galvanized steel",
    chooseFittingsNow: state.data.defaults.chooseFittingsNow || "Yes",
    inlet2: "No",
    // Positions are snapped per tank type in ensureValidSelections()
    inletPos: "Position A",
    inlet2Pos: "Position B",
    overflow2: "No",
    overflowPos: "Position 6",
    overflow2Pos: "Position 9",
    outlet2: "No",
    outlet3: "No",
    outletPos: "Position 6",
    outlet2Pos: "Position 3",
    outlet3Pos: "Position 9",
    firefit: "No",
    firefit3: "No",
    // Fire fittings sit at their own wall position (never on an outlet's)
    firefitPos: "Position 7",
    firefit3Pos: "Position 11",
    highflow1: "No",
    highflow2: "No",
    // The six pumps (PUMPS order), at most one "Yes"
    ...Object.fromEntries(PUMP_FIELDS.map((f) => [f, "No"])),
    pumpPos: "Not sure",
  };
}

function sizeMatrix() {
  return state.sizeRules?.[typeKey()] || null;
}

function syncTypeKey() {
  const typeOpt = state.data.options.type.find((o) => o.name === state.values.type);
  state.values.typeKey = typeOpt?.key || "slimline";
}

function typeKey() {
  return state.values.typeKey || "slimline";
}

function regionLabel() {
  return state.data.constraints.regionDisplay?.[state.values.region] || state.values.region;
}

function regionStepLabel() {
  return "Delivery State";
}

/** Regions offered on the live PDP for the current tank type. */
function availableRegions() {
  const names = state.data.constraints.regionsByType?.[typeKey()];
  const all = state.data.options.region || [];
  if (!names?.length) return all;
  return all.filter((o) => names.includes(o.name));
}

/** Tank types offered for the current delivery state (Round is not sold in SA). */
function availableTypes() {
  const keys = state.data.constraints.typesByRegion?.[state.values.region];
  const all = state.data.options.type || [];
  if (!keys?.length) return all;
  return all.filter((o) => keys.includes(o.key));
}

/** Urban Aquaplate PDP colour chart for Slimline / Modline / Round / Square (per state). */
function usesUrbanColourChart() {
  const key = typeKey();
  return key === "slimline" || key === "modline" || key === "round" || key === "square";
}

function urbanColourPalette() {
  return KingspanTankCore.colourPalette(state.data.constraints, typeKey(), state.values.region);
}

/** Active colour palette for the current tank type. */
function colourPalette() {
  return urbanColourPalette();
}

function findTankColour(name) {
  return KingspanTankCore.findColour(colourPalette(), name);
}

/** Map unified tankColour → Expivi base / painted fields for payload + preview. */
function syncColourFromSelection() {
  const palette = colourPalette().all;
  if (!palette.length) return;

  let c = findTankColour(state.values.tankColour);
  if (!c) {
    state.values.tankColour =
      palette.find((x) => x.name === "Galvanized steel")?.name || palette[0].name;
    c = findTankColour(state.values.tankColour);
  }
  if (!c) return;

  if (c.kind === "base") {
    state.values.Base_colours = c.name;
    state.values.paintedColor1 = "None";
  } else {
    if (!colourPalette().base.some((b) => b.name === state.values.Base_colours)) {
      state.values.Base_colours = "Galvanized steel";
    }
    state.values.paintedColor1 = c.name;
  }
}

// kx-delivery-zones.json stores zones 3-6 as bare digits and SA as "Metro (Delivered From Melbourne)";
// shoppers read "Zone 3" or "Metro, delivered from Melbourne", as on the delivery calculator page.
function deliveryZoneLabel(zone) {
  const z = String(zone || "");
  if (z.indexOf("Metro") === 0) return z.indexOf("Melbourne") !== -1 ? "Metro, delivered from Melbourne" : "Metro";
  return `Zone ${z}`;
}

function money(n) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  }).format(n || 0);
}

function num(v) {
  const n = Number(String(v ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Capacity / weight formulas from catalogue script engine */
function computeMetrics() {
  const capacity = KingspanTankCore.computeCapacity(typeKey(), state.values);
  const weight = KingspanTankCore.computeWeight(typeKey(), state.values);
  // Derived usage stats are this surface's own (the chat builder divides by
  // different constants) — only the physics lives in the core.
  return {
    capacity,
    weight,
    flushes: capacity ? Math.round((capacity / 6) * 10) / 10 : 0,
    handWatering: capacity ? Math.round(capacity / 100) / 10 : 0,
    machineLoads: capacity ? Math.round(capacity / 80) : 0,
  };
}

/** Size options from the Urban Retail Price List (assets/tank-size-rules.json).
 *  Cascade order: Width/Diameter → Length → Height, matching how the price list
 *  is published ("select the width, then the length, then read the height series").
 */
function availableHeights() {
  const list = heightsFor(state.values);
  return list.length ? list : state.data.options.height.map((o) => o.name);
}

function availableWidths() {
  const m = sizeMatrix();
  if (m?.widths) return m.widths;
  return state.data.options.width.map((o) => o.name);
}

function availableLengths() {
  const m = sizeMatrix();
  const key = typeKey();
  if (key === "slimline" || key === "modline") {
    const w = String(state.values.width || "");
    if (m?.lengthsByWidth?.[w]) return m.lengthsByWidth[w];
    if (m?.lengths) return m.lengths;
  }
  return state.data.options.length
    .map((o) => o.name)
    .filter((n) => !String(n).includes("(round)"));
}

function availableDiameters() {
  const m = sizeMatrix();
  if (typeKey() === "round" && m?.diameters) return m.diameters;
  return state.data.options.diameter.map((o) => o.name);
}

function availableSWidths() {
  const m = sizeMatrix();
  if (typeKey() === "square" && m?.widths) return m.widths;
  return state.data.options.sWidth.map((o) => o.name);
}

/** Fields visible for current type — form show rules + catalogue square width */
function visibleDimFields(v = state.values) {
  const order = ["width", "sWidth", "diameter", "length", "height"];
  return order.filter((f) => FORM_SHOW[f]?.(v));
}

function ensureInList(field, list, fallback) {
  if (!list.length) return;
  if (!list.includes(state.values[field])) {
    state.values[field] = list.includes(fallback) ? fallback : list[0];
  }
}

// --- Inlet strainer spacing (Kingspan drawing D100-018A/B) ------------------
// The Ø380 inlet hole sits 100mm from the wall/end (hole edge), so its centre is
// 290mm in. Two Ø420 strainers must stay ≥460mm centre-to-centre (40mm between
// strainers / 80mm between holes) on every tank type. Positions are computed in
// mm from the dimension that drives each type's roof layout, then compared by
// straight-line (2D) distance:
//   slimline/modline → A/B/C/E/F on the length centreline
//   round            → clock 1–12 on a circle of radius (diameter/2 − 290)
//   square           → A–H grid at ±(width/2 − 290)
const INLET_WALL_CLEARANCE_MM = 100; // wall/end → near hole edge (D100-018)
const INLET_HOLE_RADIUS_MM = 190; // Ø380 inlet hole
const INLET_END_TO_CENTRE_MM = INLET_WALL_CLEARANCE_MM + INLET_HOLE_RADIUS_MM; // 290
const INLET_MIN_SPACING_MM = 460;
function inletTypeHasSpacing(type) {
  return type === "slimline" || type === "modline" || type === "round" || type === "square";
}
function normStation(v) {
  return String(v || "").replace(/^position\s+/i, "").trim().toUpperCase();
}
// Slimline/modline A/B/C/E/F stations on the length centreline (x in mm, z = 0).
function inletStationsXmm(lengthMm) {
  const halfL = num(lengthMm) / 2;
  const end = INLET_END_TO_CENTRE_MM;
  const gap = INLET_MIN_SPACING_MM;
  const A = halfL - end;
  const C = -(halfL - end);
  const B = 0;
  const E = Math.min(A, Math.max(A - gap, B + gap));
  const F = Math.max(C, Math.min(C + gap, B - gap));
  return { A, B, C, E, F };
}
// The dimension (mm) that drives inlet layout for the current tank of this type.
function inletGeomSizeMm(type) {
  const v = state.values;
  if (type === "round") return num(v.diameter);
  if (type === "square") return num(v.sWidth);
  return num(v.length);
}
// Roof (x, z) of an inlet position in mm, or null for a non-positional option
// (e.g. "Not sure"). Geometry matches roofInletTarget in the 3D builder.
function inletPosXZ(type, sizeMm, label) {
  const k = normStation(label);
  if (type === "slimline" || type === "modline") {
    const s = inletStationsXmm(sizeMm);
    return k in s ? { x: s[k], z: 0 } : null;
  }
  if (type === "round") {
    const n = parseInt(k, 10);
    if (!(n >= 1 && n <= 12)) return null;
    const R = num(sizeMm) / 2 - INLET_END_TO_CENTRE_MM; // hole centres 290mm off the wall
    if (R <= 0) return null;
    // Round position diagram: 12 at the wall side (+z), numbered clockwise
    // seen from above (3 at -x, 6 at -z, 9 at +x) — the viewer's roundWallPoint.
    const ang = ((n - 12) / 12) * Math.PI * 2 + Math.PI / 2;
    return { x: Math.cos(ang) * R, z: Math.sin(ang) * R };
  }
  if (type === "square") {
    const s = num(sizeMm) / 2 - INLET_END_TO_CENTRE_MM; // corner/edge holes 290mm off each wall
    if (s <= 0) return null;
    const grid = {
      G: [s, s], H: [0, s], A: [-s, s], F: [s, 0],
      B: [-s, 0], E: [s, -s], D: [0, -s], C: [-s, -s],
    };
    return grid[k] ? { x: grid[k][0], z: grid[k][1] } : null;
  }
  return null;
}
function inletPosAllowed(type, sizeMm, label, otherLabel) {
  if (!otherLabel) return true;
  const a = inletPosXZ(type, sizeMm, label);
  const b = inletPosXZ(type, sizeMm, otherLabel);
  if (!a || !b) return true; // "Not sure" / unknown never blocks or is blocked
  return Math.hypot(a.x - b.x, a.z - b.z) >= INLET_MIN_SPACING_MM - 1e-6;
}
// Largest achievable spacing on this tank — the max pairwise position distance.
function maxInletSpacing(type, sizeMm, opts) {
  const pts = (opts || []).map((p) => inletPosXZ(type, sizeMm, p)).filter(Boolean);
  let max = 0;
  for (let i = 0; i < pts.length; i++)
    for (let j = i + 1; j < pts.length; j++)
      max = Math.max(max, Math.hypot(pts[i].x - pts[j].x, pts[i].z - pts[j].z));
  return max;
}

// --- Add-on eligibility (published from the tank admin via tank-catalogue.json) ---
// constraints.disabledAddonsByType = { type: { pumps: [ids], extras: [ids] } }
// constraints.firefitDisabledByType = { type: [regions] }
function addonDisabled(kind, id) {
  const d = (state.data.constraints.disabledAddonsByType || {})[typeKey()] || {};
  if ((d[kind] || []).includes(id)) return true;
  // Pumps the builder never offers on this tank type (PUMP_FIT_RULES: the garden pump on Round)
  return kind === "pumps" && !!PUMP_FIT_RULES[id]?.notOnTypes?.includes(typeKey());
}
function firefitRegionDisabled() {
  const d = (state.data.constraints.firefitDisabledByType || {})[typeKey()] || [];
  return d.includes(state.values.region);
}

/**
 * The fire fitting value for a state: the catalogue option ("Yes +$355",
 * "Yes +$774", ...) when it is one; any other "Yes…" (a link's plain "Yes",
 * or another state's option after a state change) becomes this state's "Yes"
 * option; everything else is "No". The featured cards read links the same way.
 */
function normaliseFirefit(value, region) {
  const opts = state.data.constraints.firefitByRegion?.[region] || [];
  if (!opts.length) return value || "No";
  if (opts.some((o) => o.value === value)) return value;
  const yes = opts.find((o) => o.value !== "No");
  return yes && /^\s*yes\b/i.test(String(value || "")) ? yes.value : "No";
}

/** One fire fitting's price as the cart charges it (sku map first, then the catalogue). */
function fireUnitPrice() {
  const v = state.values;
  const mapFire = Number(state.skuMap?.firefit?.[v.region]?.price);
  if (Number.isFinite(mapFire)) return mapFire;
  const fireOpts = state.data.constraints.firefitByRegion[v.region] || [];
  const fire =
    fireOpts.find((o) => o.value === v.firefit && o.value !== "No") || fireOpts.find((o) => o.value !== "No");
  return fire ? fire.price || 0 : NaN;
}

/** The size steps (a painted colour can drop, or come back, when one changes). */
const SIZE_FIELDS = ["width", "length", "height", "diameter", "sWidth"];

/** Optional fittings inside the "Optional — Extra Fittings" section (hidden by "I'll Choose Later"). */
const OPTIONAL_FITTING_FIELDS = [
  "inlet2",
  "overflow2",
  "outlet2",
  "outlet3",
  "highflow1",
  "highflow2",
  "firefit",
  "firefit3",
];
// constraints.addonMinHeightMm = { id: mm } — shortest tank an add-on is sold
// on. The Evolution MKIV's 800 mm is the 3D viewer's own limit: it won't draw
// the pump in a shorter shell, so a shorter tank greys it out. The garden
// pump's 800 mm comes from PUMP_FIT_RULES (the higher of the two wins).
function addonMinHeightMm(id) {
  const data = Number((state.data.constraints.addonMinHeightMm || {})[id]) || 0;
  return Math.max(data, Number(PUMP_FIT_RULES[id]?.minHeightMm) || 0);
}
function addonTooShort(id) {
  const min = addonMinHeightMm(id);
  return min > 0 && num(state.values.height) < min;
}
/** Display price for an add-on — the sku-map addons entry (kept in sync with the
    Shopify variant by the tank admin publish pipeline) wins over the fallback. */
function addonPrice(id, fallback) {
  return KingspanTankCore.addonPrice(state.skuMap, id, fallback);
}
const PUMP_ADDON_FIELDS = PUMP_FIELDS;

/** True when at least one CONCRETE Inlet #2 position clears the 460 mm spacing
    against Inlet #1 at its currently selected position ("Not sure" has no
    geometry and doesn't count). Types without the spacing rule are always OK. */
function inlet2PositionAvailable() {
  const key = typeKey();
  if (!inletTypeHasSpacing(key)) return true;
  if (state.values.inlet !== "Yes") return false;
  const sizeMm = inletGeomSizeMm(key);
  const opts = state.data.constraints.inletPositionByType[key] || [];
  return opts.some(
    (p) => inletPosXZ(key, sizeMm, p) && inletPosAllowed(key, sizeMm, p, state.values.inletPos)
  );
}

function ensureValidSelections() {
  const dims = visibleDimFields();
  const key = typeKey();

  // Inlet #1, Overflow #1 and Outlet #1 are mandatory on every tank — the
  // yes/no choice only applies to the additional fittings.
  state.values.inlet = "Yes";
  state.values.overflow = "Yes";
  state.values.outlet = "Yes";

  // Locked PDPs keep their tank type; clamp region instead (e.g. Round has no SA).
  if (state.lockedType) {
    const page = lockedTypePage();
    if (page) {
      state.values.type = page.name;
      syncTypeKey();
    }
    const regions = availableRegions();
    if (regions.length && !regions.some((o) => o.name === state.values.region)) {
      state.values.region = regions[0].name;
    }
  } else {
    // Hub: prefer delivery state — hide unavailable types (e.g. Round in SA)
    const types = availableTypes();
    if (types.length && !types.some((o) => o.name === state.values.type)) {
      state.values.type = types[0].name;
      syncTypeKey();
    }
    const regions = availableRegions();
    if (regions.length && !regions.some((o) => o.name === state.values.region)) {
      state.values.region = regions[0].name;
    }
  }

  // Cascade: Width/Diameter/sWidth → Length → Height
  if (dims.includes("width")) ensureInList("width", availableWidths(), state.data.defaults.width);
  if (dims.includes("diameter"))
    ensureInList("diameter", availableDiameters(), state.data.defaults.diameter);
  if (dims.includes("sWidth")) ensureInList("sWidth", availableSWidths(), state.data.defaults.sWidth);
  if (dims.includes("length")) ensureInList("length", availableLengths(), state.data.defaults.length);
  if (dims.includes("height")) ensureInList("height", availableHeights(), state.data.defaults.height);

  const inletOpts = state.data.constraints.inletPositionByType[key] || [];
  if (inletOpts.length && !inletOpts.includes(state.values.inletPos)) {
    state.values.inletPos = inletOpts[0];
  }
  if (inletOpts.length && !inletOpts.includes(state.values.inlet2Pos)) {
    state.values.inlet2Pos = inletOpts[1] || inletOpts[0];
  }
  const clockOpts = clockPositions();
  for (const posKey of ["overflowPos", "overflow2Pos", "outletPos", "outlet2Pos", "outlet3Pos"]) {
    if (!clockOpts.includes(state.values[posKey])) state.values[posKey] = clockOpts.includes("Position 6")
      ? "Position 6"
      : clockOpts[0];
  }
  // Two overflows can't share a wall position (their flanges would sit in one
  // spot). Overflow #2 defers to #1: it moves to its own default (Position 9),
  // or to #1's default (Position 6) when #1 is at 9. Covers #1 being moved
  // while #2 is off and #2 then switched on, and deep links / cart edits.
  // "Not sure" never collides; the 3D viewer gives each its default station.
  if (posTaken(state.values.overflow2Pos, state.values.overflowPos)) {
    state.values.overflow2Pos = state.values.overflowPos === "Position 9" ? "Position 6" : "Position 9";
  }
  // Fire fittings take their own wall position: never an active outlet's, and
  // never each other's. An invalid or occupied choice moves to the first free
  // clock position after Outlet #1 (so it lands beside the outlet by default).
  {
    const vv = state.values;
    const realClock = clockOpts.filter((p) => p !== "Not sure");
    const outletsInUse = [
      vv.outletPos,
      vv.outlet2 === "Yes" ? vv.outlet2Pos : "",
      vv.outlet3 === "Yes" ? vv.outlet3Pos : "",
    ];
    const startIdx = Math.max(0, realClock.indexOf(vv.outletPos));
    const ring = realClock.slice(startIdx + 1).concat(realClock.slice(0, startIdx + 1));
    const settle = (posKey, on, others) => {
      if (!on) return;
      const cur = vv[posKey];
      if (clockOpts.includes(cur) && !posTaken(cur, ...others)) return;
      const free = ring.find((p) => !posTaken(p, ...others));
      vv[posKey] = free || "Not sure";
    };
    const fire1On = !!vv.firefit && vv.firefit !== "No";
    const fire3On = vv.firefit3 === "Yes";
    settle("firefitPos", fire1On, outletsInUse);
    settle("firefit3Pos", fire3On, [...outletsInUse, fire1On ? vv.firefitPos : ""]);
  }
  const pumpOpts = state.data.constraints.pumpPositionByType[key] || [];
  if (pumpOpts.length && !pumpOpts.includes(state.values.pumpPos)) {
    state.values.pumpPos = pumpOpts.includes("Not sure") ? "Not sure" : pumpOpts[0];
  }

  // Keep a two-inlet slimline/modline setup legal: if the tank is too small to
  // hold two inlets with the 460mm gap, drop Inlet #2; otherwise nudge Inlet #2
  // to the farthest station that still clears Inlet #1 (same rule the 3D builder
  // enforces). Inlet #1 stays freely selectable; Inlet #2 defers to it.
  if (
    inletTypeHasSpacing(key) &&
    state.values.inlet === "Yes" &&
    state.values.inlet2 === "Yes"
  ) {
    const sizeMm = inletGeomSizeMm(key);
    if (maxInletSpacing(key, sizeMm, inletOpts) < INLET_MIN_SPACING_MM - 1e-6) {
      state.values.inlet2 = "No";
    } else if (!inlet2PositionAvailable()) {
      // The tank could hold two inlets, but not with Inlet #1 where it is —
      // e.g. Inlet #1 mid-tank blocks every second-inlet station. Force No;
      // the Yes chip is also greyed with an explanatory hint in the form.
      state.values.inlet2 = "No";
    } else if (!inletPosAllowed(key, sizeMm, state.values.inlet2Pos, state.values.inletPos)) {
      const l1 = state.values.inletPos;
      const p1 = inletPosXZ(key, sizeMm, l1);
      const cand = inletOpts
        .filter((p) => inletPosXZ(key, sizeMm, p) && inletPosAllowed(key, sizeMm, p, l1))
        .sort((a, b) => {
          const pa = inletPosXZ(key, sizeMm, a);
          const pb = inletPosXZ(key, sizeMm, b);
          return (
            Math.hypot(pb.x - p1.x, pb.z - p1.z) - Math.hypot(pa.x - p1.x, pa.z - p1.z)
          );
        });
      if (cand.length) state.values.inlet2Pos = cand[0];
    }
  }

  // A fire fitting asked for as "Yes" (a link, or another state's "Yes +$355")
  // becomes this state's catalogue option; anything else that isn't one is "No".
  state.values.firefit = normaliseFirefit(state.values.firefit, state.values.region);

  // One pump per tank: a link, an old cart line or a prefill with two keeps
  // the first in PUMPS order (a choice in the form already cleared the others).
  const firstPump = chosenPump();
  for (const f of PUMP_ADDON_FIELDS) {
    if (f !== firstPump && state.values[f] === "Yes") state.values[f] = "No";
  }
  // Published eligibility: anything the admin disabled (or the builder never
  // offers on this type), or a pump the tank is now too short for, snaps back to "No"
  for (const f of PUMP_ADDON_FIELDS) {
    if ((addonDisabled("pumps", f) || addonTooShort(f)) && state.values[f] === "Yes") state.values[f] = "No";
  }
  for (const f of ["inlet2", "outlet2", "outlet3", "overflow2"]) {
    if (addonDisabled("extras", f) && state.values[f] === "Yes") state.values[f] = "No";
  }
  if (addonDisabled("extras", "highflow")) {
    state.values.highflow1 = "No";
    state.values.highflow2 = "No";
  }
  if (firefitRegionDisabled()) {
    state.values.firefit = "No";
    state.values.firefit3 = "No";
  }

  // "I'll Choose Later" hides every optional fitting (the whole fittings
  // section), so none of them is priced, carted or drawn: the section shows
  // what is charged. Switching back to "Now" restores them (setValue).
  if (!showFittings()) {
    for (const f of OPTIONAL_FITTING_FIELDS) state.values[f] = "No";
  }

  // Urban colour must stay within the selected state's chart for this type,
  // and a painted colour falls back to a base one on a size Kingspan won't
  // paint (the painted swatches are greyed there).
  const palette = colourPalette().all.map((c) => c.name);
  const unpaintable = findTankColour(state.values.tankColour)?.kind === "painted" && !sizePaintable();
  if (palette.length && (unpaintable || !palette.includes(state.values.tankColour))) {
    const prev = state.values.tankColour;
    const baseNames = (colourPalette().base || []).map((c) => c.name);
    state.values.tankColour = palette.includes("Galvanized steel")
      ? "Galvanized steel"
      : baseNames[0] || palette[0];
    // Dropped only because of the size: say so next to the size, and keep it
    // to bring back on a size that can be painted.
    if (unpaintable && prev !== state.values.tankColour) {
      state.paintedWish = prev;
      state.paintNotice = {
        field: state.noticeField || "height",
        text: `${escapeHtml(prev)} isn’t available at this size (Kingspan doesn’t paint tanks ${paintLimitsText()}) — changed to ${escapeHtml(state.values.tankColour)}.`,
      };
    }
  } else if (state.paintedWish && sizePaintable() && palette.includes(state.paintedWish)) {
    const wish = state.paintedWish;
    state.paintedWish = null;
    if (findTankColour(wish)?.kind === "painted") {
      state.values.tankColour = wish;
      state.paintNotice = { field: state.noticeField || "height", text: `Back to ${escapeHtml(wish)}: this size can be painted.` };
    }
  }
  syncColourFromSelection();

  // Dependent resets when parent is No
  if (state.values.inlet === "No") {
    state.values.inlet2 = "No";
  }
  if (state.values.inlet2 !== "Yes") {
    /* keep inlet2Pos for when re-enabled */
  }
  if (state.values.overflow === "No") {
    state.values.overflow2 = "No";
    state.values.highflow1 = "No";
    state.values.highflow2 = "No";
  }
  if (state.values.overflow2 !== "Yes") {
    state.values.highflow2 = "No";
  }
  if (state.values.outlet === "No") {
    state.values.outlet2 = "No";
    state.values.outlet3 = "No";
    state.values.firefit = "No";
  }
  if (state.values.outlet3 !== "Yes") {
    state.values.firefit3 = "No";
  }
}

/**
 * The pump chips' single choice: a PUMPS field, or anything else ("none") for
 * no pump. Goes through setValue, which clears the other pumps.
 */
function choosePump(field) {
  if (PUMP_FIELDS.includes(field)) return setValue(field, "Yes");
  return setValue(chosenPump() || PUMP_FIELDS[0] || "mk3Pump", "No");
}

function setValue(key, value) {
  if (key === "pump") return choosePump(value);
  if (key === "type" && state.lockedType) return; // type is fixed on per-product PDPs
  if ((key === "inlet" || key === "overflow" || key === "outlet") && value === "No") value = "Yes"; // mandatory fittings
  const before = dockSnapshot(); // for the price dock's "what changed" note
  showAtcNotice(""); // a blocked-add notice was about the previous selection
  // Notices explain the last automatic change only: a size change keeps the
  // paint one (shown at that size field), any other choice clears both.
  const sizeKey = SIZE_FIELDS.includes(key);
  state.noticeField = sizeKey ? key : "height";
  state.paintNotice = sizeKey && state.paintNotice ? { ...state.paintNotice, field: key } : null;
  if (key === "tankColour") state.paintedWish = null; // the shopper chose a colour
  state.firefitNotice = "";
  const fireUnitBefore = key === "region" ? fireUnitPrice() : NaN;
  if (key === "chooseFittingsNow") {
    // "I'll Choose Later" drops the optional fittings (ensureValidSelections);
    // keep them aside so "I'll Choose Them Now" brings them back.
    if (value === "No" && state.values.chooseFittingsNow !== "No") {
      state.fittingsStash = Object.fromEntries(OPTIONAL_FITTING_FIELDS.map((f) => [f, state.values[f]]));
    } else if (value !== "No" && state.fittingsStash) {
      Object.assign(state.values, state.fittingsStash);
      state.fittingsStash = null;
    }
  }
  // One pump per tank: choosing one clears the others.
  if (PUMP_FIELDS.includes(key) && value === "Yes") {
    for (const f of PUMP_FIELDS) if (f !== key) state.values[f] = "No";
  }
  state.values[key] = value;
  if (key === "type") syncTypeKey();
  if (key === "tankColour") syncColourFromSelection();
  if (
    PUMP_FIELDS.includes(key) ||
    key === "type" ||
    key === "width" ||
    key === "length" ||
    key === "height" ||
    key === "diameter" ||
    key === "sWidth" ||
    key === "region" ||
    key === "tankColour" ||
    key === "inlet" ||
    key === "inlet2" ||
    key === "overflow" ||
    key === "overflow2" ||
    key === "outlet" ||
    key === "outlet2" ||
    key === "outlet3" ||
    key === "chooseFittingsNow"
  ) {
    ensureValidSelections();
    if (state.lockedType) applyLockedType();
    else if (key === "type" || key === "region") applySelectedTypeChrome();
  }
  // A fire fitting kept across a state change becomes that state's kit and
  // price: say so on the fire fitting field.
  if (key === "region") {
    const v = state.values;
    const units = (v.firefit && v.firefit !== "No" ? 1 : 0) + (v.firefit3 === "Yes" ? 1 : 0);
    const unit = fireUnitPrice();
    if (units && Number.isFinite(unit) && unit !== fireUnitBefore) {
      state.firefitNotice = `Your fire fitting now follows your delivery state: the ${escapeHtml(regionLabel())} kit, ${money(unit)}${
        units > 1 ? " each" : ""
      }.`;
    }
  }
  // The step this choice was made in, for the viewer's pump x-ray (usually
  // already posted by the pointerdown / focusin that led here).
  postKxFocus(kxFocusSectionOfField(key));
  renderAll();
  noteDockChange(before, key, value);

  // 3D viewer choreography: the positions map follows the fittings question,
  // and the camera swings to face whichever fitting position was just picked.
  if (key === "chooseFittingsNow") {
    postToKxViewer("kut:showPositions", { on: value !== "No" });
  }
  const focusKind = KUT_POS_FIELD_KIND[key];
  if (focusKind) {
    postToKxViewer("kut:focusPosition", { kind: focusKind, pos: value });
  }
  if (key === "mk3Pump" && state.values.mk3Pump === "Yes") {
    postToKxViewer("kut:focusPosition", { kind: "pump", pos: state.values.pumpPos || "" });
  }
  // An outdoor Claytech pump stands on a pad beside Outlet #1 in the viewer
  if (/^claytechC[35]X/.test(key) && state.values[key] === "Yes") {
    postToKxViewer("kut:focusPosition", { kind: "outlet", pos: state.values.outletPos || "" });
  }
  // Turning a fire fitting on swings the camera to where it was placed
  if (key === "firefit" && value && value !== "No") {
    postToKxViewer("kut:focusPosition", { kind: "firefit", pos: state.values.firefitPos || "" });
  }
  if (key === "firefit3" && value === "Yes") {
    postToKxViewer("kut:focusPosition", { kind: "firefit3", pos: state.values.firefit3Pos || "" });
  }
}

const KUT_POS_FIELD_KIND = {
  inletPos: "inlet",
  inlet2Pos: "inlet2",
  overflowPos: "overflow",
  overflow2Pos: "overflow2",
  outletPos: "outlet",
  outlet2Pos: "outlet2",
  outlet3Pos: "outlet3",
  firefitPos: "firefit",
  firefit3Pos: "firefit3",
  pumpPos: "pump",
};

function showFittings() {
  return state.values.chooseFittingsNow !== "No";
}

/**
 * Everything but the tank, per tank, at list price: painted colour (sku-map
 * surcharge lines, so the display matches checkout), extra fittings (Outlet
 * #3 has no surcharge), fire fittings (per unit, sku map first) and the pump
 * (the offers come off separately). The sum of priceItems() less the tank.
 */
function optionPriceTotal() {
  return priceItems()
    .filter((g) => g.key !== "tank")
    .reduce((total, g) => total + g.price, 0);
}

function fieldBlock(label, body, { required = false, price = null, priceText = "", hint = "" } = {}) {
  const priceHtml = priceText ? escapeHtml(priceText) : price != null ? money(price) : "";
  return `<div class="kut-field">
    <div class="kut-field__head">
      <div class="kut-field__label">${escapeHtml(label)}${required ? '<span class="req">*</span>' : ""}</div>
      ${priceHtml ? `<div class="kut-field__price">${priceHtml}</div>` : ""}
    </div>
    ${hint ? `<p class="kut-field__hint">${hint}</p>` : ""}
    ${body}
  </div>`;
}

function radioRow(options, selected, field) {
  // Match live tank PDP: native radio row for Delivery State
  return `<div class="kut-radio-row" data-field="${escapeAttr(field)}">${options
    .map((opt) => {
      const value = opt.value ?? opt.name ?? opt;
      const label = opt.label ?? opt.name ?? opt;
      const checked = String(selected) === String(value) ? "checked" : "";
      const id = `${field}-${String(value).replace(/[^\w]+/g, "-")}`;
      return `<label class="kut-radio" for="${escapeAttr(id)}">
        <input type="radio" id="${escapeAttr(id)}" name="${escapeAttr(field)}" value="${escapeAttr(
          value
        )}" ${checked} data-field="${escapeAttr(field)}" />
        <span>${escapeHtml(label)}</span>
      </label>`;
    })
    .join("")}</div>`;
}

function choiceButtons(options, selected, { meta, field, cols } = {}) {
  const gridCls = cols ? `kut-choice-grid cols-${cols}` : "kut-choice-grid";
  return `<div class="${gridCls}" ${field ? `data-field="${escapeAttr(field)}"` : ""}>${options
    .map((opt) => {
      const value = opt.value ?? opt.name ?? opt;
      const label = opt.label ?? opt.name ?? opt;
      const price = opt.price;
      const thumb = opt.thumb || "";
      const selectedCls = String(selected) === String(value) ? "is-selected" : "";
      const disabled = !!(opt && opt.disabled);
      // opt.meta: the chip's own price line ("$6,215.00", "from $1,635"); else meta() or opt.price.
      const metaText = opt.meta || meta?.(opt) || (price != null && price !== 0 ? money(price) : "");
      const thumbHtml = thumb
        ? `<span class="kut-choice__thumb"><img src="${escapeAttr(thumb)}" alt="" loading="lazy" width="64" height="64"></span>`
        : "";
      return `<button type="button" class="kut-choice ${thumb ? "kut-choice--thumb" : ""} ${selectedCls}${disabled ? " is-disabled" : ""}" data-value="${escapeAttr(value)}"${disabled ? ' disabled aria-disabled="true"' : ""}>
        ${thumbHtml}
        <span class="kut-choice__text">
          <span class="kut-choice__title">${escapeHtml(label)}</span>
          ${metaText ? `<span class="kut-choice__meta">${escapeHtml(metaText)}</span>` : ""}
        </span>
      </button>`;
    })
    .join("")}</div>`;
}

/** Size chips; priceOf(name) gives each chip's price line ("" for none). */
function dimChips(field, options, selected, { cols = 4, priceOf = null } = {}) {
  return choiceButtons(
    options.map((name) => ({
      label: `${name}mm`,
      value: name,
      meta: priceOf ? priceOf(name) : "",
    })),
    selected,
    { field, cols }
  );
}

/** AUD without the cents when there are none: "$1,635", "$1,087.50". */
function moneyShort(n) {
  return Number.isInteger(Math.round(n * 100) / 100) ? money(n).replace(/\.00$/, "") : money(n);
}

/** A priced tank's list price for these size values (the tank cart line), or NaN. */
function tankPriceFor(values) {
  if (useBaseVariantCart()) return NaN;
  const t = KingspanTankCore.resolveTank(state.skuMap, typeKey(), values);
  const n = Number(t?.price);
  return t?.variantLegacyId && Number.isFinite(n) ? n : NaN;
}

/** Height chips: the exact list price of that tank (= its cart line), e.g. "$6,215.00". */
function heightChipPrice(height) {
  const p = tankPriceFor({ ...state.values, height });
  return Number.isFinite(p) ? money(p) : "";
}

/** The size-rule heights for some size values (the same cascade as availableHeights). */
function heightsFor(values) {
  const m = sizeMatrix();
  const key = typeKey();
  if (key === "slimline" || key === "modline") {
    const w = String(values.width || "");
    const l = String(values.length || "");
    if (m?.heightsByWidthLength?.[`${w}x${l}`]) return m.heightsByWidthLength[`${w}x${l}`];
    if (m?.heightsByWidth?.[w]) return m.heightsByWidth[w];
  }
  if (key === "round" && m?.heightsByDiameter?.[String(values.diameter || "")]) {
    return m.heightsByDiameter[String(values.diameter)];
  }
  if (key === "square" && m?.heightsByWidth?.[String(values.sWidth || "")]) {
    return m.heightsByWidth[String(values.sWidth)];
  }
  return m?.heights || [];
}

/** The size-rule lengths for a slimline/modline width. */
function lengthsFor(width) {
  const m = sizeMatrix();
  return m?.lengthsByWidth?.[String(width)] || m?.lengths || [];
}

/**
 * The sizes the builder lists for the current shape and footprint (KingspanTankCore.sizeOptions,
 * the cascade heightsFor / lengthsFor use, with litres per height) plus regions: the states the
 * builder offers this shape in (the sizes are the same in each). null without size rules.
 */
function availableSizeOptions() {
  const core = window.KingspanTankCore;
  const sizes = core && typeof core.sizeOptions === "function" ? core.sizeOptions(state.sizeRules, typeKey(), state.values) : null;
  if (!sizes) return null;
  // In the catalogue's regionsByType order, as the chat builder lists them.
  const offered = availableRegions().map((o) => o.name);
  const order = state.data.constraints.regionsByType?.[typeKey()] || [];
  sizes.regions = order.filter((n) => offered.includes(n)).concat(offered.filter((n) => !order.includes(n)));
  return sizes;
}

/** Cheapest priced tank for the given size values over the heights they allow (memoised per sku map). */
const fromPriceMemo = { map: null, cache: new Map() };
function cheapestTank(values) {
  if (fromPriceMemo.map !== state.skuMap) {
    fromPriceMemo.map = state.skuMap;
    fromPriceMemo.cache = new Map();
  }
  const key = `${typeKey()}|${values.width || ""}|${values.length || ""}|${values.diameter || ""}|${values.sWidth || ""}`;
  if (!fromPriceMemo.cache.has(key)) {
    let min = NaN;
    for (const h of heightsFor(values)) {
      const p = tankPriceFor({ ...values, height: h });
      if (Number.isFinite(p) && !(p >= min)) min = p;
    }
    fromPriceMemo.cache.set(key, min);
  }
  return fromPriceMemo.cache.get(key);
}

/**
 * "from $X" for a width, length, diameter or square width chip: the cheapest
 * priced tank that choice leads to (a width: over its lengths and heights; a
 * length: over the heights at the chosen width). "" when none is priced.
 */
function fromChipPrice(field, value) {
  let min = NaN;
  if (field === "width") {
    for (const l of lengthsFor(value)) {
      const p = cheapestTank({ width: value, length: l });
      if (Number.isFinite(p) && !(p >= min)) min = p;
    }
  } else if (field === "length") {
    min = cheapestTank({ width: state.values.width, length: value });
  } else if (field === "diameter") {
    min = cheapestTank({ diameter: value });
  } else if (field === "sWidth") {
    min = cheapestTank({ sWidth: value });
  }
  return Number.isFinite(min) ? `from ${moneyShort(min)}` : "";
}

/** Colour section price: the painted surcharge this build is charged, "+ $240.00", or "Included" (as the chat builder says). */
function colourPriceText() {
  const surcharge = paintSurchargeTotal();
  return surcharge > 0 ? `+ ${money(surcharge)}` : "Included";
}

function urbanColourGrid() {
  const palette = colourPalette();
  const selected = state.values.tankColour;
  const paintable = sizePaintable();
  const paintedPrice = paintedColourPrice();
  const chips = palette.all
    .map((o) => {
      const sel = o.name === selected ? "is-selected" : "";
      const painted = o.kind === "painted";
      const off = painted && !paintable;
      const chipStyle =
        o.name === "Galvanized steel" || o.name === "Galvanised steel"
          ? `style="background:linear-gradient(135deg,#d8dbd6,#aeb3ad 55%,#8e948e)"`
          : o.hex
            ? `style="background:${o.hex}"`
            : `style="background:#9aa0a6"`;
      const tip = !painted
        ? `${o.name} — base colour, included`
        : off
          ? `${o.name} — painted colour, not available at this size`
          : `${o.name} — painted colour${paintedPrice > 0 ? `, + ${money(paintedPrice)}` : ""}`;
      return `<button type="button" class="kut-swatch ${sel}" data-field="tankColour" data-value="${escapeAttr(
        o.name
      )}" title="${escapeAttr(tip)}" aria-label="${escapeAttr(tip)}"${off ? ' disabled aria-disabled="true"' : ""}>
        <span class="kut-swatch__chip" ${chipStyle}></span>
      </button>`;
    })
    .join("");
  const current = findTankColour(selected);
  return `
    <div class="kut-colour">
      <div class="kut-colour__grid">${chips}</div>
      <div class="kut-colour__meta">
        <span>${escapeHtml(selected || "Select a colour")}${current?.kind === "painted" ? " — painted colour" : ""}</span>
        <strong>${escapeHtml(colourPriceText())}</strong>
      </div>
    </div>`;
}

/** The sizes Kingspan won't paint, for this type (HTML). */
function paintLimitsText() {
  const { maxHeightMm, maxDiameterMm } = KingspanTankCore.PAINT_LIMITS;
  return typeKey() === "round"
    ? `taller than ${maxHeightMm}&nbsp;mm or wider than ${maxDiameterMm}&nbsp;mm in diameter`
    : `taller than ${maxHeightMm}&nbsp;mm`;
}

/** Why the painted swatches are greyed (sizes Kingspan won't paint), else "". */
function colourHint() {
  if (sizePaintable() || !(colourPalette().painted || []).length) return "";
  return `Painted colours aren’t available on tanks ${paintLimitsText()} — base colours only at this size.`;
}

/** The paint notice (HTML) for one field: the size field the shopper changed, or "colour". */
function paintNoticeHint(field) {
  const n = state.paintNotice;
  if (!n) return "";
  return field === "colour" || n.field === field ? n.text : "";
}

/** priceText: the section's subtotal per tank ("+ $655.00"), shown in the header; "" for none. */
function accordion(id, title, body, { secondary = false, priceText = "" } = {}) {
  const open = isAccordionOpen(id);
  const cls = `kut-accordion${secondary ? " kut-accordion--secondary" : ""}`;
  return `<details class="${cls}" data-kut-accordion="${escapeAttr(id)}"${open ? " open" : ""}>
    <summary class="kut-accordion__summary">
      <span class="kut-accordion__title">${escapeHtml(title)}</span>
      ${priceText ? `<span class="kut-accordion__price">${escapeHtml(priceText)}</span>` : ""}
      <span class="kut-accordion__icon" aria-hidden="true"></span>
    </summary>
    <div class="kut-accordion__body">${body}</div>
  </details>`;
}

/** Static "Included" marker for mandatory fittings (no yes/no choice). */
function includedBadge() {
  return '<div class="kut-choice-grid cols-2"><span class="kut-choice is-selected kut-choice--static">Included</span></div>';
}

/** Hover/focus details for each pump (shown via an info tooltip). */
const KUT_PUMP_INFO = {
  mk3Pump:
    "The Evolution MkIV pump system combines a Claytech submersible booster pump with built-in mains rainwater switching. Suitable for multi-storey homes. Max Flow (LPM): 100 · Max Height (M): 46 · Max Taps: 8",
  integratedGardenPump:
    "A submersible pump system that sits inside the tank, with a bucket-high garden tap — for outdoor use and medium irrigation.",
  claytechC3X:
    "Self-priming Claytech C3X pressure pump, installed beside the tank. For single-storey household supply, granny flats and external taps. Max Taps: 3",
  claytechC3X20mmAcqua:
    "The Claytech C3X pressure pump with a 20 mm mains switching device, which switches to mains water when the tank runs low. For single-storey household supply, granny flats and external taps. Max Taps: 3",
  claytechC5X:
    "Self-priming Claytech C5X pressure pump, installed beside the tank. For double-storey household supply, granny flats and external taps. Max Taps: 5",
  claytechC5X20mmAcqua:
    "The Claytech C5X pressure pump with a 20 mm mains switching device, which switches to mains water when the tank runs low. For double-storey household supply, granny flats and external taps. Max Taps: 5",
};

/** State-specific fire fitting kits (from the catalogue region keys). */
const KUT_FIREFIT_INFO = {
  "NSW / ACT": "65 mm Storz coupling fire fitting — NSW / ACT specification.",
  SA: "65 mm Storz coupling fire fitting — SA specification.",
  QLD: "50 mm camlock coupling fire fitting — QLD specification.",
  VIC: "65 mm CFA 3-thread coupling fire fitting — VIC (CFA) specification.",
};

function yesNo(field, { yesLabel = "Yes", noLabel = "No", yesPrice = 0, yesDisabled = false } = {}) {
  const selected = state.values[field] || "No";
  const options = [
    {
      label: yesLabel,
      value: "Yes",
      price: yesPrice,
      disabled: yesDisabled,
    },
    { label: noLabel, value: "No", price: 0 },
  ];
  return choiceButtons(options, selected, {
    field,
    cols: 2,
    meta: (opt) => (opt.value === "Yes" && yesPrice ? money(yesPrice) : ""),
  });
}

function addonChip(field, label, price, { disabled = false } = {}) {
  const on = state.values[field] === "Yes";
  const thumb = thumbUrl(ADDON_THUMB_KEYS[field] || "");
  const thumbHtml = thumb
    ? `<span class="kut-choice__thumb"><img src="${escapeAttr(thumb)}" alt="" loading="lazy" width="64" height="64"></span>`
    : "";
  return `<div class="kut-choice-grid cols-2" data-field="${escapeAttr(field)}">
    <button type="button" class="kut-choice ${thumb ? "kut-choice--thumb" : ""} ${on ? "is-selected" : ""}${disabled ? " is-disabled" : ""}" data-value="Yes"${disabled ? ' disabled aria-disabled="true"' : ""}>
      ${thumbHtml}
      <span class="kut-choice__text">
        <span class="kut-choice__title">${escapeHtml(label)}</span>
        <span class="kut-choice__meta">${money(price)}</span>
      </span>
    </button>
    <button type="button" class="kut-choice ${!on ? "is-selected" : ""}" data-value="No">
      <span class="kut-choice__title">No thanks</span>
    </button>
  </div>`;
}

function positionDiagramUrl() {
  const key = typeKey();
  const t = shopifyCfg.thumbs || {};
  if (key === 'slimline') return t.diagramSlimline || '';
  if (key === 'modline') return t.diagramModline || '';
  if (key === 'round') return t.diagramRound || '';
  if (key === 'square') return t.diagramSquare || '';
  return '';
}

function positionDiagramHtml(caption = 'Fitting positions') {
  const url = positionDiagramUrl();
  if (!url) return '';
  return `<div class="kut-pos-diagram">
    <img src="${escapeAttr(url)}" alt="${escapeAttr(caption)}" loading="lazy" />
    <p class="kut-pos-diagram__caption">${escapeHtml(caption)}</p>
  </div>`;
}

function clockPositions() {
  return [...Array.from({ length: 12 }, (_, i) => `Position ${i + 1}`), "Not sure"];
}

/** True when `pos` is a real position already used by one of `others`. */
function posTaken(pos, ...others) {
  return pos !== "Not sure" && others.some((o) => o && o !== "Not sure" && o === pos);
}

/* ---- Info tooltips ------------------------------------------------------
 * One shared bubble on <body> (position:fixed): the pump accordion clips with
 * overflow:hidden and the PDP wraps #kut-root in a transformed scroll-reveal
 * ancestor, so an in-place bubble gets cut off. Prefers above the icon, flips
 * below when there's no room, clamps to the visible viewport (minus a docked
 * AI chat and the price dock), and points the arrow at the icon. Mouse hover,
 * keyboard focus (Esc closes), tap to toggle. */
const infoTip = { el: null, anchor: null, hideTimer: 0, raf: 0 };

function infoTipEl() {
  if (!infoTip.el) {
    const el = document.createElement("div");
    el.id = "kutTip";
    el.className = "kut-tip";
    el.hidden = true;
    // Screen readers get the text via the trigger's aria-describedby.
    el.setAttribute("aria-hidden", "true");
    el.addEventListener("mouseenter", () => clearTimeout(infoTip.hideTimer));
    el.addEventListener("mouseleave", () => hideInfoTip(120));
    document.body.appendChild(el);
    infoTip.el = el;
  }
  return infoTip.el;
}

function placeInfoTip() {
  const btn = infoTip.anchor;
  const el = infoTip.el;
  if (!btn || !el || !btn.isConnected) return hideInfoTip();
  const GAP = 9;
  const PAD = 8;
  const r = btn.getBoundingClientRect();
  // A docked AI chat reflows <body> with padding-right and covers that strip.
  const right =
    document.documentElement.clientWidth - (parseFloat(getComputedStyle(document.body).paddingRight) || 0);
  // The price dock covers the bottom of the viewport while it is stuck there.
  const dock = dockRectOnScreen();
  const bottom = dock && dock.top > 0 ? dock.top : window.innerHeight;
  if (r.bottom < 0 || r.top > bottom) return hideInfoTip();
  el.style.maxWidth = `${Math.max(160, right - PAD * 2)}px`;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const roomAbove = r.top - PAD;
  const roomBelow = bottom - PAD - r.bottom;
  const below = roomAbove < h + GAP && roomBelow > roomAbove;
  const cx = r.left + r.width / 2;
  const left = Math.min(Math.max(cx - w / 2, PAD), right - PAD - w);
  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(below ? r.bottom + GAP : r.top - GAP - h)}px`;
  el.style.setProperty("--kut-tip-arrow", `${Math.round(Math.min(Math.max(cx - left, 12), w - 12))}px`);
  el.classList.toggle("is-below", below);
}

function showInfoTip(btn) {
  const src = document.getElementById(btn.dataset.kutTip || "");
  if (!src) return;
  const el = infoTipEl();
  clearTimeout(infoTip.hideTimer);
  infoTip.anchor = btn;
  el.textContent = src.textContent;
  el.hidden = false;
  placeInfoTip();
  el.classList.add("is-open");
}

function hideInfoTip(delay = 0) {
  clearTimeout(infoTip.hideTimer);
  const close = () => {
    if (infoTip.el) {
      infoTip.el.classList.remove("is-open");
      infoTip.el.hidden = true;
    }
    infoTip.anchor = null;
  };
  if (delay) infoTip.hideTimer = setTimeout(close, delay);
  else close();
}

/** Delegated once on #builderForm (from boot), so it survives the innerHTML re-renders. */
function bindInfoTips() {
  const form = document.getElementById("builderForm");
  if (!form) return;
  const tipBtn = (e) => (e.target instanceof Element ? e.target.closest(".kut-info[data-kut-tip]") : null);
  let tapToggle = false;
  form.addEventListener("pointerover", (e) => {
    const b = tipBtn(e);
    if (b && e.pointerType === "mouse") showInfoTip(b);
  });
  form.addEventListener("pointerout", (e) => {
    const b = tipBtn(e);
    if (b && e.pointerType === "mouse" && !b.contains(e.relatedTarget)) hideInfoTip(120);
  });
  form.addEventListener("pointerdown", (e) => {
    const b = tipBtn(e);
    tapToggle = !!b && e.pointerType !== "mouse" && infoTip.anchor === b;
  });
  form.addEventListener("click", (e) => {
    const b = tipBtn(e);
    if (!b) return;
    // Touch: a second tap closes. Keyboard Enter/Space (detail 0): toggles.
    if (tapToggle || (e.detail === 0 && infoTip.anchor === b)) hideInfoTip();
    else showInfoTip(b);
    tapToggle = false;
  });
  const keyboardFocus = (el) => {
    try {
      return el.matches(":focus-visible");
    } catch (_) {
      return true; // Safari < 15.4: no :focus-visible (taps don't focus buttons there)
    }
  };
  form.addEventListener("focusin", (e) => {
    const b = tipBtn(e);
    if (b && keyboardFocus(b)) showInfoTip(b);
  });
  form.addEventListener("focusout", (e) => {
    if (tipBtn(e)) hideInfoTip(120);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && infoTip.anchor) hideInfoTip();
  });
  document.addEventListener("pointerdown", (e) => {
    if (!infoTip.anchor || tipBtn(e) || (infoTip.el && infoTip.el.contains(e.target))) return;
    hideInfoTip();
  });
  const follow = () => {
    if (!infoTip.anchor || infoTip.raf) return;
    infoTip.raf = requestAnimationFrame(() => {
      infoTip.raf = 0;
      placeInfoTip();
    });
  };
  window.addEventListener("scroll", follow, { passive: true, capture: true });
  window.addEventListener("resize", follow);
}

/** An (i) button and its hidden text for a pump (the bubble lives on <body>, see bindInfoTips). */
function pumpInfoTip(field) {
  const info = KUT_PUMP_INFO[field] || "";
  if (!info) return "";
  const tipId = `kutTipText-${field}`;
  return `<button type="button" class="kut-info kut-pump__info" data-kut-tip="${escapeAttr(tipId)}" aria-label="${escapeAttr(
    `More about the ${PUMP_SHORT[field] || ADDON_LABELS[field] || field}`
  )}" aria-describedby="${escapeAttr(tipId)}">i</button><span class="kut-tooltip" id="${escapeAttr(tipId)}" hidden>${escapeHtml(info)}</span>`;
}

/**
 * One unit of a pump after the pump offer, when the offer takes something off
 * it on this build (priced tank, a cart line to discount, at or above its list
 * price, a variant the offer names, an eligible state); else NaN. For the
 * chip's "$1,125.00 with your tank" — the saving itself shows only in the dock.
 */
function pumpOfferNet(field) {
  if (tankPriceUnavailable() || addonTooShort(field)) return NaN;
  const entry = state.skuMap?.addons?.[field];
  const unit = Number(entry?.price);
  if (!entry?.variantLegacyId || !Number.isFinite(unit)) return NaN;
  const net = KingspanTankCore.promoNetPrice(promoPolicy(), typeKey(), state.values.region, field, unit, state.skuMap);
  return Number.isFinite(net) && net < unit ? net : NaN;
}

/**
 * The pump section's choice: the six pumps in PUMPS order (one the builder
 * doesn't offer on this tank type is left out — the garden pump on Round) and
 * "No pump". Each chip shows its list price (= its cart line), "$X with your
 * tank" while the pump offer takes something off it, or why it's greyed (a
 * tank too short for it); each pump has an (i) bubble. One choice: picking a
 * pump clears the others (choosePump).
 */
function pumpChoiceBlock(fields) {
  const chosen = chosenPump();
  const chip = (f) => {
    const on = chosen === f;
    const short = addonTooShort(f);
    const net = pumpOfferNet(f);
    const thumb = thumbUrl(ADDON_THUMB_KEYS[f] || "");
    return `<div class="kut-pump">
      <button type="button" class="kut-choice kut-choice--pump${thumb ? " kut-choice--thumb" : ""}${on ? " is-selected" : ""}${short ? " is-disabled" : ""}" data-value="${escapeAttr(f)}" aria-pressed="${on}"${short ? ' disabled aria-disabled="true"' : ""}>
        ${thumb ? `<span class="kut-choice__thumb"><img src="${escapeAttr(thumb)}" alt="" loading="lazy" width="64" height="64"></span>` : ""}
        <span class="kut-choice__text">
          <span class="kut-choice__title">${escapeHtml(PUMP_SHORT[f] || ADDON_LABELS[f] || f)}</span>
          <span class="kut-choice__meta">${money(pumpPrice(f))}</span>
          ${Number.isFinite(net) ? `<span class="kut-choice__net">${money(net)} with your tank</span>` : ""}
          ${short ? `<span class="kut-choice__note">Needs a tank ${addonMinHeightMm(f)}&nbsp;mm+ tall</span>` : ""}
        </span>
      </button>
      ${pumpInfoTip(f)}
    </div>`;
  };
  const none = `<div class="kut-pump">
      <button type="button" class="kut-choice kut-choice--pump${chosen ? "" : " is-selected"}" data-value="none" aria-pressed="${!chosen}">
        <span class="kut-choice__title">No pump</span>
      </button>
    </div>`;
  // Hints: the pumps this tank is too short for, then the pump offer (while it takes something off one).
  const short = fields.filter((f) => addonTooShort(f));
  const hints = [];
  if (short.length) {
    const names = short.map((f) => `the ${escapeHtml(PUMP_SHORT[f] || f)}`);
    const min = Math.max(...short.map((f) => addonMinHeightMm(f)));
    const who = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
    hints.push(
      `${who.charAt(0).toUpperCase()}${who.slice(1)} need${short.length > 1 ? "" : "s"} a tank at least ${min}&nbsp;mm tall — choose a taller tank to add ${short.length > 1 ? "one" : "it"}.`
    );
  }
  const offerField = fields.find((f) => Number.isFinite(pumpOfferNet(f)));
  const offerHint = offerField
    ? KingspanTankCore.promoHint(promoPolicy(), typeKey(), state.values.region, offerField, Number(state.skuMap.addons[offerField].price), state.skuMap)
    : "";
  if (offerHint) hints.push(`${escapeHtml(offerHint)} (one pump per tank). Taken off in your total.`);
  return `<div class="kut-field kut-field--pumps">
    <div class="kut-field__head">
      <div class="kut-field__label" id="kutPumpLabel">Choose a pump</div>
    </div>
    ${hints.map((h) => `<p class="kut-field__hint">${h}</p>`).join("")}
    <div class="kut-choice-grid cols-2 kut-pump-grid" data-field="pump" role="group" aria-labelledby="kutPumpLabel">
      ${fields.map(chip).join("")}${none}
    </div>
  </div>`;
}

/** Height is chosen last, so name the dimensions it is constrained by. */
function heightStepLabel(key, v) {
  if (key === "round") {
    return `Choose Your Tank Height (${v.diameter || "—"}mm Diameter)`;
  }
  if (key === "square") {
    return `Choose Your Tank Height (${v.sWidth || "—"}mm Width)`;
  }
  return `Choose Your Tank Height (${v.width || "—"}mm Width × ${v.length || "—"}mm Length)`;
}

/* ============ Preset (curated deep link) summary card ============ */

function renderPresetSummary() {
  const v = state.values;
  const sel = getSelection() || {};
  const colour = findTankColour(v.tankColour);

  const fittings = showFittings()
    ? [`Inlet #1 — ${v.inletPos}`, `Overflow #1 — ${v.overflowPos}`, `Outlet #1 — ${v.outletPos}`]
    : ["Inlet #1, Overflow #1 and Outlet #1 — positions confirmed later"];
  if (v.inlet2 === "Yes") fittings.push(`Inlet #2 — ${v.inlet2Pos}`);
  if (v.overflow2 === "Yes") fittings.push(`Overflow #2 — ${v.overflow2Pos}`);
  if (v.outlet2 === "Yes") fittings.push(`Outlet #2 — ${v.outlet2Pos}`);
  if (v.outlet3 === "Yes") fittings.push(`Outlet #3 — ${v.outlet3Pos}`);

  const addonRows = [];
  const pushAddon = (label, price) => {
    addonRows.push(
      `<li>${escapeHtml(label)}${
        Number.isFinite(price) && price > 0 ? `<span class="kut-preset__price">+ ${money(price)}</span>` : ""
      }</li>`
    );
  };
  for (const field of TRACKED_ADDON_FIELDS) {
    // The three extra-fitting fields already read in the fittings list above
    if (field === "inlet2" || field === "overflow2" || field === "outlet2" || field === "outlet3") continue;
    if (v[field] === "Yes") pushAddon(ADDON_LABELS[field] || field, addonPrice(field, NaN));
  }
  const fireUnits = (v.firefit && v.firefit !== "No" ? 1 : 0) + (v.firefit3 === "Yes" ? 1 : 0);
  if (fireUnits) {
    // Same map-first pricing as optionPriceTotal, so the chip matches checkout
    const unit = fireUnitPrice();
    pushAddon(
      `${ADDON_LABELS.firefit} — ${regionLabel() || v.region}${fireUnits > 1 ? ` × ${fireUnits}` : ""}`,
      Number.isFinite(unit) ? unit * fireUnits : NaN
    );
  }

  // Map-gated like the Total below: only a surcharge the cart actually charges
  const paintedSurcharge = paintSurchargeTotal();
  const rows = [
    [
      "Tank",
      `${escapeHtml(v.type || "")}${sel.size ? ` · ${escapeHtml(sel.size)}` : ""}${
        sel.capacityL ? ` · ≈${Number(sel.capacityL).toLocaleString("en-AU")} L` : ""
      }`,
    ],
    [
      "Colour",
      `${
        colour?.hex
          ? `<span class="kut-preset__swatch" style="background:${escapeAttr(colour.hex)}"></span>`
          : ""
      }${escapeHtml(v.tankColour || "")}${
        paintedSurcharge ? `<span class="kut-preset__price">+ ${money(paintedSurcharge)}</span>` : ""
      }`,
    ],
    ["Delivery state", escapeHtml(regionLabel() || v.region || "")],
    ["Fittings", `<ul class="kut-preset__list">${fittings.map((f) => `<li>${escapeHtml(f)}</li>`).join("")}</ul>`],
  ];
  if (addonRows.length) {
    rows.push(["Add-ons", `<ul class="kut-preset__list">${addonRows.join("")}</ul>`]);
  }
  const offerLines = promoOfferLines(); // [] when the size has no price
  if (offerLines.length) {
    rows.push([
      offerLines.length > 1 ? "Offers" : "Offer",
      `<ul class="kut-preset__deals">${offerLines
        .map(
          (l) =>
            `<li><span class="kut-preset__deal">${escapeHtml(l.text)}</span>` +
            `<span class="kut-preset__price">−${money(l.amount)}</span></li>`
        )
        .join("")}</ul>` +
        `<span class="kut-preset__dealnote">${
          offerLines.some((l) => l.offer === "tank")
            ? "Colour, fittings and fire fittings are full price."
            : "The tank, colour, fittings and fire fittings are full price."
        }</span>`,
    ]);
  }

  return `
    <div class="kut-preset" id="kutPresetSummary">
      <div class="kut-preset__head">
        <p class="kut-preset__eyebrow">Pre-configured tank</p>
        <h3 class="kut-preset__name">${escapeHtml(state.preset?.name || v.type || "Your tank")}</h3>
      </div>
      <dl class="kut-preset__rows">
        ${rows
          .map(
            ([label, body]) =>
              `<div class="kut-preset__row"><dt>${label}</dt><dd>${body}</dd></div>`
          )
          .join("")}
      </dl>
      <p class="kut-preset__note">Happy with it? Add it to your cart below — or tailor any option first.</p>
      <button type="button" class="kut-preset__edit" id="kutPresetEdit">Edit this tank</button>
    </div>
  `;
}

function renderForm() {
  const form = document.getElementById("builderForm");
  if (!form) return;
  hideInfoTip(); // innerHTML below replaces the tooltip's anchor

  // Curated preset link: show the summary card until "Edit this tank".
  if (state.preset && !state.preset.editing) {
    form.innerHTML = renderPresetSummary();
    const editBtn = document.getElementById("kutPresetEdit");
    if (editBtn) {
      editBtn.addEventListener("click", () => {
        state.preset.editing = true;
        renderAll();
      });
    }
    return;
  }
  // Keep open/closed across full form rebuilds (selection re-renders)
  captureAccordionState(form);
  const key = typeKey();
  const v = state.values;
  const label = regionLabel();
  const regions = availableRegions();
  const types = availableTypes();
  const heights = availableHeights();
  const parts = [];

  parts.push(
    fieldBlock(
      regionStepLabel(),
      radioRow(
        regions.map((o) => ({
          label: state.data.constraints.regionDisplay?.[o.name] || o.name,
          value: o.name,
        })),
        v.region,
        "region"
      ),
      { required: true }
    )
  );

  // Shared hub shows type picker; locked PDPs hide it (type is the page).
  if (!state.lockedType) {
    parts.push(
      fieldBlock(
        "Choose Tank Type",
        choiceButtons(
          types.map((o) => ({
            label: o.name,
            value: o.name,
            thumb: thumbUrl(TYPE_THUMB_KEYS[o.name] || o.key || ""),
          })),
          v.type,
          { field: "type", cols: 2 }
        ),
        { required: true }
      )
    );
  } else {
    parts.push(
      `<div class="kut-field kut-field--locked-type">
        <div class="kut-field__label">Tank type</div>
        <p class="locked-type-badge">${escapeHtml(v.type)}</p>
      </div>`
    );
  }

  parts.push(
    fieldBlock(`Choose Your Preferred Tank Colour`, urbanColourGrid(), {
      required: true,
      priceText: colourPriceText(),
      hint: paintNoticeHint("colour") || colourHint(), // the notice already names the size limits
    })
  );

  // Price-list order: width (or diameter) → length → height. Each chip says
  // what it costs: "from $X" (the cheapest priced tank it leads to) on width,
  // length and diameter; the exact tank price (= the cart line) on height.
  const from = (field) => (name) => fromChipPrice(field, name);
  if (FORM_SHOW.width(v)) {
    parts.push(
      fieldBlock(
        "Choose Your Tank Width",
        dimChips("width", availableWidths(), v.width, { priceOf: from("width") }),
        { required: true, hint: paintNoticeHint("width") }
      )
    );
  }
  if (FORM_SHOW.sWidth(v)) {
    parts.push(
      fieldBlock(
        "Choose Your Tank Width",
        dimChips("sWidth", availableSWidths(), v.sWidth, { priceOf: from("sWidth") }),
        { required: true, hint: paintNoticeHint("sWidth") }
      )
    );
  }
  if (FORM_SHOW.diameter(v)) {
    parts.push(
      fieldBlock(
        "Choose Your Tank Diameter",
        dimChips("diameter", availableDiameters(), v.diameter, { priceOf: from("diameter") }),
        { required: true, hint: paintNoticeHint("diameter") }
      )
    );
  }
  if (FORM_SHOW.length(v)) {
    parts.push(
      fieldBlock(
        `Choose Your Tank Length (${v.width || "—"}mm Width)`,
        dimChips("length", availableLengths(), v.length, { cols: 3, priceOf: from("length") }),
        { required: true, hint: paintNoticeHint("length") }
      )
    );
  }

  parts.push(
    fieldBlock(
      heightStepLabel(key, v),
      dimChips("height", heights, v.height, { priceOf: heightChipPrice }),
      { required: true, hint: paintNoticeHint("height") }
    )
  );

  parts.push(
    fieldBlock(
      "Would You Like To Choose Your Fittings Positions?",
      radioRow(
        [
          { label: "I'll Choose Them Now", value: "Yes" },
          { label: "I'll Choose Later", value: "No" },
        ],
        v.chooseFittingsNow || "Yes",
        "chooseFittingsNow"
      ),
      {
        required: true,
        hint:
          v.chooseFittingsNow === "No"
            ? "Inlet #1, Overflow #1 and Outlet #1 are included and we\u2019ll confirm their positions with you. To add extra fittings or fire fittings, choose them now."
            : "",
      }
    )
  );

  const fireOpts = state.data.constraints.firefitByRegion[v.region] || [];
  // Fire fitting chips show the unit price the cart charges (sku map first).
  const fireUnit = fireUnitPrice();
  const fireYes = fireOpts.find((o) => o.value !== "No") || null;
  const fireChipOpts = fireOpts.map((o) =>
    o.value !== "No" && Number.isFinite(fireUnit) ? { ...o, price: fireUnit } : o
  );

  if (showFittings()) {
    const inletPos = state.data.constraints.inletPositionByType[key] || [];
    const clockPos = clockPositions();
    // Wall positions already used by outlets / fire fittings — each picker greys
    // the others' seats (fittings can't share a position).
    const outletPosInUse = [
      v.outletPos,
      v.outlet2 === "Yes" ? v.outlet2Pos : "",
      v.outlet3 === "Yes" ? v.outlet3Pos : "",
    ].filter(Boolean);
    const firePosInUse = [
      v.firefit && v.firefit !== "No" && !firefitRegionDisabled() ? v.firefitPos : "",
      v.outlet3 === "Yes" && v.firefit3 === "Yes" ? v.firefit3Pos : "",
    ].filter(Boolean);
    const fittingsBody = [
      positionDiagramHtml(
        key === 'slimline' || key === 'modline'
          ? 'Positions — wall side at top (12 = left end, 6 = right end)'
          : key === 'square'
            ? 'Positions — wall side at 12; letters A–H on the roof grid'
            : 'Positions — wall side at 12 (clock face)'
      ),
      fieldBlock("Inlet #1", includedBadge()),
      v.inlet !== "No"
        ? fieldBlock(
            "Inlet #1 position",
            choiceButtons(
              inletPos.map((p) => ({ label: p, value: p })),
              v.inletPos || inletPos[0],
              { field: "inletPos", cols: 3 }
            )
          )
        : "",
      v.inlet !== "No" && !addonDisabled("extras", "inlet2")
        ? fieldBlock(
            "Inlet #2",
            yesNo("inlet2", {
              yesPrice: addonPrice("inlet2", 33),
              yesDisabled: !inlet2PositionAvailable(),
            }),
            {
              hint: !inlet2PositionAvailable()
                ? "No second-inlet position clears the 460&nbsp;mm spacing with Inlet #1 where it is — move Inlet #1 toward an end of the tank to add Inlet #2."
                : "",
            }
          )
        : "",
      v.inlet !== "No" && v.inlet2 === "Yes"
        ? fieldBlock(
            "Inlet #2 position",
            choiceButtons(
              inletPos.map((p) => ({
                label: p,
                value: p,
                disabled:
                  inletTypeHasSpacing(key) &&
                  !inletPosAllowed(key, inletGeomSizeMm(key), p, v.inletPos),
              })),
              v.inlet2Pos || inletPos[1] || inletPos[0],
              { field: "inlet2Pos", cols: 3 }
            ),
            {
              hint: inletTypeHasSpacing(key)
                ? "Greyed positions sit too close to Inlet #1 — inlet strainers must be at least 460&nbsp;mm apart (40&nbsp;mm between strainers / 80&nbsp;mm between holes)."
                : "",
            }
          )
        : "",
      fieldBlock("Overflow #1", includedBadge()),
      v.overflow !== "No"
        ? fieldBlock(
            "Overflow #1 position",
            choiceButtons(
              clockPos.map((p) => ({ label: p, value: p, disabled: posTaken(p, v.overflow2 === "Yes" ? v.overflow2Pos : "") })),
              v.overflowPos || "Position 6",
              { field: "overflowPos", cols: 4 }
            ),
            { hint: v.overflow2 === "Yes" ? "Greyed positions are taken by the other overflow." : "" }
          )
        : "",
      v.overflow !== "No" && !addonDisabled("extras", "highflow")
        ? fieldBlock(
            "Highflow upgrade for Overflow #1",
            addonChip("highflow1", "Add Highflow", addonPrice("highflow1", 231)),
            { hint: "Upgrades Overflow #1 to a highflow overflow for faster discharge in heavy rain." }
          )
        : "",
      v.overflow !== "No" && !addonDisabled("extras", "overflow2") ? fieldBlock("Overflow #2", yesNo("overflow2", { yesPrice: addonPrice("overflow2", 23) })) : "",
      v.overflow !== "No" && v.overflow2 === "Yes"
        ? fieldBlock(
            "Overflow #2 position",
            choiceButtons(
              clockPos.map((p) => ({ label: p, value: p, disabled: posTaken(p, v.overflowPos) })),
              v.overflow2Pos || "Position 9",
              { field: "overflow2Pos", cols: 4 }
            ),
            { hint: "Greyed positions are taken by the other overflow — fittings can\u2019t share a position." }
          )
        : "",
      v.overflow !== "No" && v.overflow2 === "Yes" && !addonDisabled("extras", "highflow")
        ? fieldBlock(
            "Highflow upgrade for Overflow #2",
            addonChip("highflow2", "Add Highflow", addonPrice("highflow2", 231)),
            { hint: "Upgrades Overflow #2 to a highflow overflow for faster discharge in heavy rain." }
          )
        : "",
      fieldBlock("Outlet #1", includedBadge()),
      v.outlet !== "No"
        ? fieldBlock(
            "Outlet #1 position",
            choiceButtons(
              clockPos.map((p) => ({
                label: p,
                value: p,
                disabled: posTaken(p, v.outlet2 === "Yes" ? v.outlet2Pos : "", v.outlet3 === "Yes" ? v.outlet3Pos : "", ...firePosInUse),
              })),
              v.outletPos || "Position 6",
              { field: "outletPos", cols: 4 }
            ),
            { hint: v.outlet2 === "Yes" || v.outlet3 === "Yes" || firePosInUse.length ? "Greyed positions are taken by another outlet or a fire fitting." : "" }
          )
        : "",
      v.outlet !== "No" && !firefitRegionDisabled()
        ? fieldBlock(
            `Fire fitting for Outlet #1 (${v.region})`,
            choiceButtons(fireChipOpts, v.firefit || "No", { field: "firefit", cols: 2 }),
            { hint: [state.firefitNotice, (KUT_FIREFIT_INFO[v.region] || "").trim()].filter(Boolean).join(" ") }
          )
        : "",
      v.outlet !== "No" && !firefitRegionDisabled() && v.firefit && v.firefit !== "No"
        ? fieldBlock(
            "Fire fitting position",
            choiceButtons(
              clockPos.map((p) => ({
                label: p,
                value: p,
                disabled: posTaken(p, ...outletPosInUse, v.firefit3 === "Yes" ? v.firefit3Pos : ""),
              })),
              v.firefitPos || "Position 7",
              { field: "firefitPos", cols: 4 }
            ),
            { hint: "Where the fire fitting goes on the tank wall. Greyed positions are taken by an outlet or the other fire fitting." }
          )
        : "",
      v.outlet !== "No" && !addonDisabled("extras", "outlet2") ? fieldBlock("Outlet #2", yesNo("outlet2", { yesPrice: addonPrice("outlet2", 69) })) : "",
      v.outlet !== "No" && v.outlet2 === "Yes"
        ? fieldBlock(
            "Outlet #2 position",
            choiceButtons(
              clockPos.map((p) => ({
                label: p,
                value: p,
                disabled: posTaken(p, v.outletPos, v.outlet3 === "Yes" ? v.outlet3Pos : "", ...firePosInUse),
              })),
              v.outlet2Pos || "Position 3",
              { field: "outlet2Pos", cols: 4 }
            ),
            { hint: "Greyed positions are taken by another outlet — fittings can\u2019t share a position." }
          )
        : "",
      v.outlet !== "No" && !addonDisabled("extras", "outlet3") ? fieldBlock("Outlet #3", yesNo("outlet3")) : "",
      v.outlet !== "No" && v.outlet3 === "Yes"
        ? fieldBlock(
            "Outlet #3 position",
            choiceButtons(
              clockPos.map((p) => ({
                label: p,
                value: p,
                disabled: posTaken(p, v.outletPos, v.outlet2 === "Yes" ? v.outlet2Pos : "", ...firePosInUse),
              })),
              v.outlet3Pos || "Position 9",
              { field: "outlet3Pos", cols: 4 }
            ),
            { hint: "Greyed positions are taken by another outlet — fittings can\u2019t share a position." }
          )
        : "",
      // Priced like Outlet #1's fire fitting: same state option label and unit price.
      v.outlet !== "No" && v.outlet3 === "Yes" && !firefitRegionDisabled()
        ? fieldBlock(
            "Fire fitting for Outlet #3",
            yesNo("firefit3", { yesLabel: fireYes?.label || "Yes", yesPrice: Number.isFinite(fireUnit) ? fireUnit : 0 })
          )
        : "",
      v.outlet !== "No" && v.outlet3 === "Yes" && v.firefit3 === "Yes"
        ? fieldBlock(
            "Fire fitting position (Outlet #3)",
            choiceButtons(
              clockPos.map((p) => ({
                label: p,
                value: p,
                disabled: posTaken(p, ...outletPosInUse, v.firefit && v.firefit !== "No" ? v.firefitPos : ""),
              })),
              v.firefit3Pos || "Position 11",
              { field: "firefit3Pos", cols: 4 }
            ),
            { hint: "Where this fire fitting goes on the tank wall. Greyed positions are taken by an outlet or the other fire fitting." }
          )
        : "",
    ]
      .filter(Boolean)
      .join("");
    // Header subtotal: the extra fittings and fire fittings, per tank, at list price.
    const fittingsSubtotal = priceItems()
      .filter((g) => g.key === "fittings" || g.key === "firefit")
      .reduce((s, g) => s + g.price, 0);
    parts.push(
      accordion("fittings", "Optional — Extra Fittings", fittingsBody, {
        priceText: fittingsSubtotal > 0 ? `+ ${money(fittingsSubtotal)}` : "",
      })
    );
  }

  const pumpPos = state.data.constraints.pumpPositionByType[key] || [];
  // Six pumps, one per tank (PUMPS order). Eligibility per tank type is
  // published from the tank admin (disabledAddonsByType) plus PUMP_FIT_RULES.
  const pumpFields = PUMP_FIELDS.filter((f) => !addonDisabled("pumps", f));
  if (pumpFields.length) {
    const pump = chosenPump();
    const pumpBody = [
      pumpChoiceBlock(pumpFields),
      // Only the in-tank Evolution MKIV has a position to choose
      pump === "mk3Pump"
        ? fieldBlock(
            "Submersible pump position",
            choiceButtons(
              pumpPos.map((pp) => ({ label: pp, value: pp })),
              v.pumpPos || pumpPos[0],
              { field: "pumpPos", cols: 3 }
            )
          )
        : "",
    ]
      .filter(Boolean)
      .join("");
    parts.push(
      accordion("pump", "Optional — Pump with Tank Purchase", pumpBody, {
        priceText: pump ? `+ ${money(pumpPrice(pump))}` : "",
      })
    );
  }

  const focused = focusedFormControl(form);
  form.innerHTML = parts.join("");
  bindFormEvents();
  restoreFormFocus(form, focused);
}

/**
 * The form control that has the keyboard focus, as { field, value } (a chip,
 * swatch or radio: its group's data-field and its value) or { accordion }
 * (an accordion header); null when the focus is not in the form. Every
 * choice rebuilds the form, so renderForm puts the focus back on the same
 * control and a keyboard user's next Tab carries on from there.
 */
function focusedFormControl(form) {
  const el = document.activeElement;
  if (!el || el === form || !form.contains(el)) return null;
  const holder = el.closest("[data-field]");
  if (holder) {
    const radio = el.tagName === "INPUT" && String(el.getAttribute("type")).toLowerCase() === "radio";
    const value = radio ? el.value : el.getAttribute("data-value");
    if (value != null) return { field: holder.getAttribute("data-field"), value: String(value) };
  }
  const acc = el.tagName === "SUMMARY" ? el.closest("details[data-kut-accordion]") : null;
  return acc ? { accordion: acc.getAttribute("data-kut-accordion") } : null;
}

/** Focus the control focusedFormControl() named, or the chosen one of its group if it is gone or disabled. */
function restoreFormFocus(form, key) {
  if (!key) return;
  const usable = (el) => !!el && !el.hasAttribute("disabled");
  let target = null;
  if (key.accordion != null) {
    const acc = Array.from(form.querySelectorAll("details[data-kut-accordion]")).find((d) => d.getAttribute("data-kut-accordion") === key.accordion);
    target = acc ? acc.querySelector("summary") : null;
  } else {
    const inGroup = Array.from(form.querySelectorAll('[data-value], input[type="radio"]')).filter((el) => {
      const holder = el.closest("[data-field]");
      return !!holder && holder.getAttribute("data-field") === key.field;
    });
    const valueOf = (el) => (el.tagName === "INPUT" ? el.value : el.getAttribute("data-value"));
    target = inGroup.find((el) => String(valueOf(el)) === key.value) || null;
    if (!usable(target)) target = inGroup.find((el) => usable(el) && (el.checked || el.classList.contains("is-selected"))) || null;
  }
  if (!usable(target)) return;
  // Putting the focus back is not the shopper moving to that field's step (in
  // Safari a click leaves the focus where it was, maybe on another step's field).
  kxFocus.restoring = true;
  try {
    target.focus({ preventScroll: true });
  } catch (e) {
    target.focus();
  } finally {
    kxFocus.restoring = false;
  }
}

function bindFormEvents() {
  const form = document.getElementById("builderForm");
  if (!form) return;

  form.querySelectorAll('input[type="radio"][data-field]').forEach((input) => {
    input.addEventListener("change", () => setValue(input.dataset.field, input.value));
  });

  form.querySelectorAll(".kut-swatch").forEach((btn) => {
    btn.addEventListener("click", () => setValue(btn.dataset.field, btn.dataset.value));
  });

  form.querySelectorAll(".kut-choice-grid[data-field]").forEach((grid) => {
    const field = grid.dataset.field;
    grid.querySelectorAll(".kut-choice").forEach((btn) => {
      btn.addEventListener("click", () => setValue(field, btn.dataset.value));
    });
  });

  form.querySelectorAll("details[data-kut-accordion]").forEach((el) => {
    el.addEventListener("toggle", () => {
      const id = el.dataset.kutAccordion;
      if (!id) return;
      // Every re-render re-creates the open accordions, which fires "toggle"
      // with nothing changed: only the shopper opening or closing one is a
      // step change for the 3D viewer.
      const changed = el.open !== isAccordionOpen(id);
      state.accordions[id] = el.open;
      if (changed) postKxFocus(id, el.open);
    });
  });
}

const kxViewer = {
  ready: false,
  frame: null,
  root: null,
  // The framed viewer's origin (initKxViewer, from the URL it loads): messages
  // go only to that origin, and only messages from it are heard (runbook B6).
  origin: "",
  lastSent: "",
};

function buildKxViewerPayload() {
  const v = state.values;
  const colour = findTankColour(v.tankColour);
  // Only the dimensions this tank type is built from (width/length for
  // slimline/modline, sWidth for square, diameter for round). The others keep
  // their catalogue defaults in state, and the viewer reads sWidth as the
  // width, so sending them would redraw the tank at the wrong size.
  const dims = visibleDimFields();
  const dim = (field) => (dims.includes(field) ? v[field] : undefined);
  return {
    type: typeKey(),
    height: v.height,
    width: dim("width"),
    length: dim("length"),
    diameter: dim("diameter"),
    sWidth: dim("sWidth"),
    colour: v.tankColour,
    hex: colour?.hex || undefined,
    inlet: v.inlet,
    inlet2: v.inlet2,
    inletPos: v.inletPos,
    inlet2Pos: v.inlet2Pos,
    overflow: v.overflow,
    overflow2: v.overflow2,
    overflowPos: v.overflowPos,
    overflow2Pos: v.overflow2Pos,
    outlet: v.outlet,
    outlet2: v.outlet2,
    outlet3: v.outlet3,
    outletPos: v.outletPos,
    outlet2Pos: v.outlet2Pos,
    outlet3Pos: v.outlet3Pos,
    firefit: v.firefit,
    firefit3: v.firefit3,
    firefitPos: v.firefitPos,
    firefit3Pos: v.firefit3Pos,
    highflow1: v.highflow1,
    highflow2: v.highflow2,
    // The six pumps (the viewer draws one: the MKIV in the tank at pumpPos, the
    // garden pump on the tank floor, a Claytech on a pad beside Outlet #1)
    ...Object.fromEntries(PUMP_FIELDS.map((f) => [f, v[f] === "Yes" ? "Yes" : "No"])),
    pumpPos: v.pumpPos,
  };
}

function postToKxViewer(type, payload) {
  const win = kxViewer.frame?.contentWindow;
  if (!win || !kxViewer.origin) return;
  win.postMessage({ source: "kut-shopify", type, payload }, kxViewer.origin);
}

/**
 * Which step of the form the shopper is on, for the 3D viewer (tester round
 * 3): it shows the tank walls see-through, so an in-tank pump (the Evolution
 * MKIV, the Integrated Garden Pump) can be seen, only while the shopper is on
 * the pump step, and solid again once they go back to the colour, the size or
 * the fittings. kut:focus { section, open }: the accordion's id ("pump",
 * "fittings") for anything inside one (open: false when the shopper closes
 * it), else the field's section (kxFocusSectionOfField). Posted on a real
 * accordion open/close (bindFormEvents), when the shopper presses, taps or
 * tabs into a field (bindKxFocus) and with every choice (setValue), once per
 * change of section. The step is kept from the first touch, so a viewer that
 * gets ready later (or reloads) is told it on kut:ready / kut:pong; the
 * viewer's own "See inside" button sends kut:focusReset, after which the
 * next touch is posted even on the same step. An older viewer ignores both.
 */
const KUT_FOCUS_SECTION_BY_FIELD = {
  region: "region",
  type: "type",
  tankColour: "colour",
  width: "size",
  sWidth: "size",
  length: "size",
  diameter: "size",
  height: "size",
  chooseFittingsNow: "fittings",
};
// current: the shopper's step, { section, open }, from their first touch (the
// viewer may not be ready yet); sent: the step the viewer was last told, as
// postKxFocus keys it (null: tell it again); restoring: renderForm is putting
// the keyboard focus back (restoreFormFocus), which is not the shopper moving.
const kxFocus = { current: null, sent: null, restoring: false };

/** A field's section: "pump" for the pump choice and position, the fittings' for the rest of the optional fittings. */
function kxFocusSectionOfField(field) {
  if (!field) return null;
  if (field === "pump" || field === "pumpPos" || PUMP_FIELDS.includes(field)) return "pump";
  return KUT_FOCUS_SECTION_BY_FIELD[field] || "fittings";
}

/** The section a form element belongs to (see above), or null (an accordion header, the gaps between fields). */
function kxFocusSectionOf(el) {
  if (!(el instanceof Element)) return null;
  // Headers open/close their accordion: "toggle" says which, after the click.
  if (el.closest("summary")) return null;
  const acc = el.closest("details[data-kut-accordion]");
  if (acc) return acc.dataset.kutAccordion || null;
  const holder = el.closest("[data-field]") || el.closest(".kut-field")?.querySelector("[data-field]");
  return kxFocusSectionOfField(holder ? holder.getAttribute("data-field") : "");
}

function postKxFocus(section, open = true) {
  if (!section) return;
  kxFocus.current = { section, open: !!open };
  flushKxFocus();
}

/** Tell the viewer the shopper's step when it has changed since it was last told (and once it is ready). */
function flushKxFocus() {
  const cur = kxFocus.current;
  if (!cur || !kxViewer.ready) return;
  const key = cur.open ? cur.section : "";
  if (key === kxFocus.sent) return;
  kxFocus.sent = key;
  postToKxViewer("kut:focus", { section: cur.section, open: cur.open });
}

/** Delegated once on #builderForm (from boot), so it survives the innerHTML re-renders. */
function bindKxFocus() {
  const form = document.getElementById("builderForm");
  if (!form) return;
  const onEnter = (e) => postKxFocus(kxFocusSectionOf(e.target));
  // A touch that starts a scroll fires pointerdown too (then pointercancel),
  // and on a phone the viewer sits above the form: swiping back up to it over
  // the fields must not move the step. A touch counts on its click (a tap),
  // a mouse or pen press at once.
  form.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "touch") onEnter(e);
  });
  form.addEventListener("click", onEnter);
  form.addEventListener("focusin", (e) => {
    if (!kxFocus.restoring) onEnter(e);
  });
}

function syncKxViewer(force = false) {
  if (!kxViewer.frame || !kxViewer.ready) return;
  const payload = buildKxViewerPayload();
  const key = JSON.stringify(payload);
  if (!force && key === kxViewer.lastSent) return;
  kxViewer.lastSent = key;
  postToKxViewer("kut:setConfig", payload);
}

/**
 * The 3D viewer's URL (a URL object, or null when there is none): the theme
 * setting kx_3d_viewer_url (`configured`: shopifyCfg.kxViewerUrl, or the
 * preview's data-viewer-url), or a ?kx_viewer= override for trying another
 * viewer build. Tester round 4 review: the override used to be framed as
 * given, and frame.src = "javascript:…//" ran script in the store's own origin
 * (a crafted link was a reflected XSS on the product page). Only http(s) URLs
 * are framed now, and the override only when it is on the configured viewer's
 * origin or this page's own; anything else falls back to the setting. The
 * URL's origin is then the only one the bridge talks to (kxViewer.origin, B6).
 */
function kxViewerUrlFor(search, configured) {
  const httpUrl = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return null;
    try {
      const u = new URL(text, location.origin);
      return u.protocol === "https:" || u.protocol === "http:" ? u : null;
    } catch (_) {
      return null;
    }
  };
  const base = httpUrl(configured);
  const override = httpUrl(new URLSearchParams(search || "").get("kx_viewer"));
  if (override && (override.origin === location.origin || (base && override.origin === base.origin))) {
    return override;
  }
  return base;
}

function initKxViewer() {
  const root = document.getElementById("kutKxPreview");
  const frame = document.getElementById("kutKxFrame");
  const status = document.getElementById("kutKxStatus");
  if (!root || !frame) return;

  const url = kxViewerUrlFor(location.search, shopifyCfg.kxViewerUrl || root.dataset.viewerUrl || "");
  if (!url) {
    root.classList.add("is-missing");
    root.hidden = true;
    return;
  }
  url.searchParams.set("embed", "1");
  const boot = buildKxViewerPayload();
  Object.entries(boot).forEach(([k, val]) => {
    if (val != null && val !== "") url.searchParams.set(k, String(val));
  });

  kxViewer.root = root;
  kxViewer.frame = frame;
  kxViewer.origin = url.origin;
  root.hidden = false;

  window.addEventListener("message", (event) => {
    const data = event.data;
    // Only the viewer's own origin: another window on the page (or one that
    // framed it) cannot fake kut:ready or kut:focusReset.
    if (event.origin !== kxViewer.origin) return;
    if (!data || data.source !== "kx-tank-viewer") return;
    // The chat's own viewer posts to this window too: the step is this one's.
    const ownViewer = event.source === frame.contentWindow;
    if (data.type === "kut:ready" || data.type === "kut:pong") {
      kxViewer.ready = true;
      root.classList.add("is-ready");
      if (status) status.textContent = "";
      syncKxViewer(true);
      if (ownViewer) {
        // A (re)loaded viewer starts on no step: tell it the shopper's, after the build.
        kxFocus.sent = null;
        flushKxFocus();
      }
    } else if (data.type === "kut:focusReset" && ownViewer) {
      // "See inside" in the viewer: its walls no longer follow the step it was told.
      kxFocus.sent = null;
    }
  });

  frame.addEventListener("load", () => {
    // Ping in case ready fired before listener
    setTimeout(() => postToKxViewer("kut:ping"), 300);
  });

  frame.src = url.toString();
  if (status) status.textContent = "Loading 3D preview…";
}

function renderPreview() {
  const v = state.values;
  const key = typeKey();
  const tank = document.getElementById("tankVisual");

  const colour = findTankColour(v.tankColour);
  const base = state.data.materials.baseColour?.options.find((o) => o.name === v.Base_colours);
  const paint = state.data.materials.paintedColour?.options.find((o) => o.name === v.paintedColor1);
  const color =
    colour?.hex ||
    (paint && paint.name !== "None" && paint.hex) ||
    base?.hex ||
    "#8d9490";

  if (tank) {
    tank.className = `tank ${key}`;
    const body = document.getElementById("tankBody");
    const lid = document.getElementById("tankLid");
    if (body) {
      body.style.background = `linear-gradient(160deg, ${lighten(color, 28)}, ${color} 45%, ${darken(
        color,
        18
      )})`;
    }
    if (lid) {
      lid.style.background = `linear-gradient(90deg, ${lighten(color, 10)}, ${darken(color, 10)})`;
    }
    const h = num(v.height) || 1000;
    const scale = Math.min(1.25, Math.max(0.75, h / 1600));
    tank.style.transform = `scale(${scale})`;
  }

  const dims = visibleDimFields()
    .map((f) => v[f])
    .filter(Boolean)
    .join(" × ");
  const caption = document.getElementById("previewCaption");
  if (caption) caption.textContent = `${v.type} · ${dims} mm · ${v.tankColour}`;

  const m = computeMetrics();
  const setText = (id, text) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };
  // Live under-gallery usage stats: Width / Length|Diameter / Height / usage
  const dim1Label = document.getElementById("kutStatDim1Label");
  const dim2Label = document.getElementById("kutStatDim2Label");
  if (key === "round") {
    if (dim1Label) dim1Label.textContent = "Diameter";
    if (dim2Label) dim2Label.textContent = "—";
    setText("kutStatDim1", v.diameter || "—");
    setText("kutStatDim2", "—");
  } else if (key === "square") {
    if (dim1Label) dim1Label.textContent = "Width";
    if (dim2Label) dim2Label.textContent = "Width";
    setText("kutStatDim1", v.sWidth || "—");
    setText("kutStatDim2", v.sWidth || "—");
  } else {
    if (dim1Label) dim1Label.textContent = "Width";
    if (dim2Label) dim2Label.textContent = "Length";
    setText("kutStatDim1", v.width || "—");
    setText("kutStatDim2", v.length || "—");
  }
  setText("kutStatHeight", v.height || "—");
  setText(
    "kutStatCapacity",
    m.capacity ? Math.round(Number(m.capacity)).toLocaleString("en-AU") : "—"
  );
  setText("kutStatFlushes", m.flushes ? `${m.flushes}` : "—");
  setText("kutStatLoads", m.machineLoads ? `${m.machineLoads}` : "—");
  setText("kutStatWatering", m.handWatering ? `${m.handWatering}` : "—");

  // Legacy hooks
  setText("metricCapacity", `${m.capacity.toLocaleString()}`);
  setText("metricWeight", `${m.weight.toLocaleString()}`);
  setText("metricFlushes", `${m.flushes}`);
  setText("metricWatering", `${m.handWatering}`);
  setText("metricLoads", `${m.machineLoads}`);

  // Options, Product and the offer line are per tank; Total is for the quantity.
  const summary = priceSummary();
  const { qty, options, product, promo, discount, total } = summary;
  // No size price to charge: add to cart is blocked, so never show the fixed
  // BYO base price (e.g. $890) as if it were this tank's price.
  const unpriced = tankPriceUnavailable();
  const totalText = unpriced ? PRICE_ON_ENQUIRY : money(total);
  setText("optionsPrice", money(options));
  setText("productPrice", unpriced ? PRICE_ON_ENQUIRY : money(product));
  // One line per offer (tank, the pump), each naming its eligible states.
  const discountLines = document.getElementById("discountLines");
  if (discountLines) {
    const lines = discount > 0 && !unpriced ? promoOfferLines(promo) : [];
    discountLines.innerHTML = lines
      .map(
        (l) =>
          `<div class="price-line price-line--discount" data-offer="${escapeAttr(l.offer)}"><span>${escapeHtml(
            l.text
          )}</span><strong>−${money(l.amount)}</strong></div>`
      )
      .join("");
    discountLines.hidden = !lines.length;
  }
  const perTank = document.getElementById("perTankNote");
  if (perTank) {
    perTank.textContent = qty > 1 ? `Per tank: the total is for all ${qty} tanks` : "";
    perTank.hidden = !(qty > 1);
  }
  setText("totalLabel", qty > 1 ? `Total (${qty} tanks)` : "Total");
  setText("totalPrice", totalText);
  renderDock(dockModel(summary));
  setText("displayPrice", unpriced ? PRICE_ON_ENQUIRY : `${money(total)} AUD`);

  // Keep Dawn's product price block in sync when present
  document.querySelectorAll(".price-item--regular, .price__regular .price-item").forEach((el) => {
    el.textContent = totalText;
  });

  const title = document.getElementById("productTitle");
  if (title && shopifyCfg.productTitle) title.textContent = shopifyCfg.productTitle;

  const d = state.delivery;
  const hasEstimate = !!(d && Number.isFinite(d.price));
  const deliveryLine = document.getElementById("deliveryLine");
  if (deliveryLine) {
    if (hasEstimate) {
      setText("deliveryLineLabel", `Delivery to ${d.postcode} (estimate)`);
      setText("deliveryLineAmount", money(d.price));
      deliveryLine.hidden = false;
    } else {
      deliveryLine.hidden = true;
    }
  }
  const deliveryStatus = document.getElementById("kutDeliveryStatus");
  if (deliveryStatus) {
    if (!d) {
      deliveryStatus.textContent = "";
    } else if (d.unavailable) {
      deliveryStatus.textContent = `Delivery estimates are unavailable right now — freight to ${d.postcode} is confirmed with your order.`;
    } else if (d.notServiced) {
      deliveryStatus.textContent = `We don't have standard delivery pricing for ${d.postcode} — we'll confirm freight by quote after you order, or contact us first.`;
    } else {
      deliveryStatus.textContent = `Estimated delivery to ${d.postcode}: ${money(d.price)} (${deliveryZoneLabel(d.zone)}). Confirmed with your order.`;
    }
  }

  const note = document.getElementById("deliveryNote");
  const noteVisible = document.getElementById("kutDeliveryNoteVisible");
  let deliveryText = `Delivery — Approx. 2 to 4 Weeks (${regionLabel() || "AU"})`;
  if (hasEstimate) {
    deliveryText += ` · est. ${money(d.price)} to ${d.postcode}`;
  }
  if (note) note.textContent = deliveryText;
  if (noteVisible) noteVisible.textContent = deliveryText;

  syncKxViewer();
  renderStaffLink(); // every choice and quantity change (staff only)
}

/* ---- Price dock ----------------------------------------------------------
 * Sticky at the bottom of the configurator column at every width (inside
 * #kut-root, between the form and #priceBreakdown — no move to <body>): the
 * estimated total, "You save $X · <states> delivery only" while an offer takes
 * something off (never in SA, never when the offers are off or ended), a
 * short "what changed" note after a choice, "See breakdown" (Tank, Colour,
 * Extra fittings, Fire fitting, Pump, each offer, Total — the same rows and
 * cents as the cart less the KPO discounts) and Add to cart (the buy box's
 * button, so the whole validation path runs). It fades while the breakdown
 * below is on screen. Savings appear only here; the chips show list prices.
 */
const dockUi = { open: false, resting: false, lastTotal: null, noteTimer: 0, liveTimer: 0, pendingNote: "" };

const signedMoney = (cents) => `${cents < 0 ? "−" : "+"} ${money(Math.abs(cents) / 100)}`;

function dockRowHtml(row) {
  const amount =
    row.key === "tank" || row.key === "total"
      ? money(row.cents / 100)
      : row.cents < 0
        ? `−${money(-row.cents / 100)}`
        : `+ ${money(row.cents / 100)}`;
  const label = row.detail ? `${row.label} · ${row.detail}` : row.label;
  const cls = row.key === "offer" ? " kut-dock__item--offer" : row.key === "total" ? " kut-dock__item--total" : "";
  return `<div class="kut-dock__item${cls}" data-dock-row="${escapeAttr(row.key)}"${
    row.offer ? ` data-offer="${escapeAttr(row.offer)}"` : ""
  }><span>${escapeHtml(label)}</span><span>${escapeHtml(amount)}</span></div>`;
}

function renderDock(model) {
  const dock = document.getElementById("kutDock");
  if (!dock) return;
  const qtyText = model.qty > 1 ? ` (${model.qty} tanks)` : "";
  const label = document.getElementById("kutDockLabel");
  if (label) {
    label.innerHTML = `<span class="kut-dock__label-long">Estimated total${qtyText}</span><span class="kut-dock__label-short" aria-hidden="true">Est. total${qtyText}</span>`;
  }
  const totalEl = document.getElementById("kutStickyTotal");
  if (totalEl) totalEl.textContent = model.totalText;
  dock.classList.toggle("is-unpriced", model.unpriced);
  const save = document.getElementById("kutDockSave");
  if (save) {
    save.hidden = !(model.saveCents > 0);
    save.innerHTML =
      model.saveCents > 0
        ? `You save ${escapeHtml(money(model.saveCents / 100))}${
            model.terms ? ` <span class="kut-dock__terms">· ${escapeHtml(model.terms)}</span>` : ""
          }`
        : "";
  }
  const toggle = document.getElementById("kutDockToggle");
  const items = document.getElementById("kutDockItems");
  if (toggle) toggle.hidden = model.unpriced;
  if (model.unpriced) dockUi.open = false;
  if (items) {
    items.innerHTML = model.unpriced
      ? ""
      : (model.qty > 1 ? `<p class="kut-dock__qty">For ${model.qty} tanks</p>` : "") +
        model.rows.map(dockRowHtml).join("") +
        dockRowHtml({ key: "total", label: "Estimated total", detail: "", cents: model.totalCents });
  }
  setDockOpen(dockUi.open);
  // Screen readers hear a total change once it settles (not on the first render).
  const announce = `${model.totalText}|${model.saveCents}`;
  if (dockUi.lastTotal !== null && dockUi.lastTotal !== announce) scheduleDockAnnouncement(model);
  dockUi.lastTotal = announce;
}

function setDockOpen(open) {
  dockUi.open = !!open;
  const toggle = document.getElementById("kutDockToggle");
  const items = document.getElementById("kutDockItems");
  if (toggle) {
    toggle.setAttribute("aria-expanded", String(dockUi.open));
    toggle.textContent = dockUi.open ? "Hide breakdown" : "See breakdown";
  }
  if (items) items.hidden = !dockUi.open;
  document.getElementById("kutDock")?.classList.toggle("is-open", dockUi.open);
}

/** One polite announcement per settled change: the note (if any), the total and the saving. */
function scheduleDockAnnouncement(model) {
  clearTimeout(dockUi.liveTimer);
  dockUi.liveTimer = setTimeout(() => {
    const live = document.getElementById("kutDockLive");
    if (!live) return;
    const m = model || dockModel();
    const parts = [];
    if (dockUi.pendingNote) parts.push(dockUi.pendingNote);
    parts.push(`Estimated total${m.qty > 1 ? ` for ${m.qty} tanks` : ""} ${m.totalText}.`);
    if (m.saveCents > 0) parts.push(`You save ${money(m.saveCents / 100)}${m.terms ? `, ${m.terms}` : ""}.`);
    live.textContent = parts.join(" ");
    dockUi.pendingNote = "";
  }, 600);
}

/** What the "what changed" note compares: per-tank cart lines and offers, and the total. */
function dockSnapshot() {
  if (!state.data) return null;
  const unpriced = tankPriceUnavailable();
  const lines = {};
  for (const g of priceItems()) {
    if (g.key === "tank") continue;
    for (const l of g.lines) lines[l.field] = { label: l.label, cents: Math.round(l.price * 100) };
  }
  const p = promoParts();
  return {
    unpriced,
    totalCents: Math.round(priceSummary().total * 100),
    lines,
    offers: { tank: Math.round((p.tank || 0) * 100), ...Object.fromEntries(Object.entries(p.addons || {}).map(([f, a]) => [f, Math.round(a * 100)])) },
  };
}

/** Why this build can't have pump f ('' when it can, or f is not a pump): its type or its height. */
function pumpDropReason(f) {
  if (!PUMP_FIELDS.includes(f)) return "";
  if (addonDisabled("pumps", f)) return `not offered on ${state.values.type} tanks`;
  const min = addonMinHeightMm(f);
  if (min > 0 && num(state.values.height) < min) return `needs a tank ${min} mm or taller`;
  return "";
}

/** The changed choice in a few words, for a note that only gives the new total. */
function changeLabel(key, value) {
  const v = state.values;
  if (key === "region") return `Delivery to ${regionLabel()}`;
  if (key === "type") return `${v.type} tank`;
  if (key === "qty") return `${value} tank${Number(value) > 1 ? "s" : ""}`;
  if (key === "tankColour") return `Colour ${v.tankColour}`;
  if (key === "chooseFittingsNow") return value === "No" ? "Fittings chosen later" : "Fittings chosen now";
  const dim = { width: "Width", length: "Length", height: "Height", diameter: "Diameter", sWidth: "Width" }[key];
  if (dim) return `${dim} ${v[key]} mm`;
  return "Your build";
}

/**
 * After a choice: a short note above the dock saying what it did to the
 * price, e.g. "Added Claytech C3X + $660.00 · 10% off with your tank
 * −$66.00" (on a phone: "Added Claytech C3X · total + $594.00"), "Removed
 * Extra Outlet #2 − $69.00", "Height 2320 mm · total + $710.00". Nothing when
 * the total and the lines didn't change, or the size has no price. Offer
 * wording only when that offer took something off (so never in SA).
 */
function noteDockChange(before, key, value) {
  const after = dockSnapshot();
  if (!before || !after || before.unpriced || after.unpriced) return;
  const delta = after.totalCents - before.totalCents;
  const added = Object.keys(after.lines).filter((f) => !(f in before.lines));
  const removed = Object.keys(before.lines).filter((f) => !(f in after.lines));
  const net = ` · total ${signedMoney(delta)}`;
  let head = "";
  let detail = "";
  if (added.length === 1 && removed.length === 0) {
    const f = added[0];
    const off = after.offers[f] || 0;
    const offerLabel = promoPolicy()[KingspanTankCore.promoOfferKey(f)]?.label || "Offer";
    head = `Added ${after.lines[f].label}`;
    detail = `+ ${money(after.lines[f].cents / 100)}${off > 0 ? ` · ${offerLabel} −${money(off / 100)}` : ""}`;
  } else if (removed.length === 1 && added.length === 0) {
    const f = removed[0];
    // A pump the builder dropped because the new size or type doesn't take it: say why.
    const why = key !== f && key !== "pump" ? pumpDropReason(f) : "";
    head = why ? `${changeLabel(key, value)} · ${before.lines[f].label} removed (${why})` : `Removed ${before.lines[f].label}`;
    detail = `− ${money(before.lines[f].cents / 100)}`;
  } else if (added.length === 1 && removed.length === 1 && PUMP_FIELDS.includes(added[0])) {
    head = `Changed to ${after.lines[added[0]].label}`;
  } else if (delta !== 0) {
    head = changeLabel(key, value);
  } else {
    return;
  }
  // Wide screens show the detail; a phone shows the net change of the total instead.
  showDockNote(head, detail, delta !== 0 ? net : "");
}

function showDockNote(head, detail, net) {
  const el = document.getElementById("kutDockNote");
  const plain = `${head}${detail ? ` ${detail}` : ""}${!detail && net ? net : ""}`;
  dockUi.pendingNote = `${plain}.`;
  scheduleDockAnnouncement();
  if (!el) return;
  el.innerHTML = `<b>${escapeHtml(head)}</b>${detail ? ` <span class="kut-dock__note-detail">${escapeHtml(detail)}</span>` : ""}${
    net ? `<span class="kut-dock__note-net">${escapeHtml(net)}</span>` : ""
  }`;
  el.classList.toggle("has-detail", !!detail);
  el.hidden = false;
  el.classList.add("is-shown");
  clearTimeout(dockUi.noteTimer);
  dockUi.noteTimer = setTimeout(() => {
    el.classList.remove("is-shown");
    el.hidden = true;
  }, 5000);
}

/** The dock while it is on screen and covering the viewport's bottom edge (stuck), else null. */
function dockRectOnScreen() {
  const dock = document.getElementById("kutDock");
  if (!dock || dock.hidden || dock.classList.contains("is-resting") || !dock.offsetHeight) return null;
  const r = dock.getBoundingClientRect();
  return r.top < window.innerHeight && r.bottom > 0 ? r : null;
}

/** Keyboard focus never lands hidden under the stuck dock: scroll the focused field clear of it. */
function keepClearOfDock(el) {
  const d = dockRectOnScreen();
  if (!d || !el?.getBoundingClientRect || el.closest?.("#kutDock")) return;
  const r = el.getBoundingClientRect();
  if (r.bottom > d.top - 8 && r.top < d.bottom) window.scrollBy(0, r.bottom - d.top + 16);
}

function applyDockResting() {
  const dock = document.getElementById("kutDock");
  if (!dock) return;
  // Never hide the dock under the KEYBOARD focus (it would drop focus); a
  // mouse or tap leaves focus on the button it pressed, which must not keep
  // the dock over the breakdown.
  const active = document.activeElement;
  let keyboard = false;
  if (active && dock.contains(active)) {
    try {
      keyboard = active.matches(":focus-visible");
    } catch (_) {
      keyboard = false;
    }
  }
  dock.classList.toggle("is-resting", dockUi.resting && !keyboard);
}

/**
 * Show the dock (after a successful boot), proxy its Add to cart to the buy
 * box's button, wire "See breakdown" (Escape closes it), and fade the dock
 * while #priceBreakdown is on screen.
 */
function bindDock() {
  const dock = document.getElementById("kutDock");
  if (!dock) return;
  dock.hidden = false;
  const proxy = document.getElementById("kutStickyAtcBtn");
  const real = document.getElementById("btnAddToCart");
  proxy?.addEventListener("click", () => {
    if (!real || real.disabled) return;
    real.click();
  });
  const toggle = document.getElementById("kutDockToggle");
  toggle?.addEventListener("click", () => setDockOpen(!dockUi.open));
  dock.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && dockUi.open) {
      setDockOpen(false);
      toggle?.focus();
    }
  });
  dock.addEventListener("focusout", () => setTimeout(applyDockResting, 0));
  const breakdown = document.getElementById("priceBreakdown");
  if (breakdown && typeof IntersectionObserver !== "undefined") {
    const io = new IntersectionObserver(
      (entries) => {
        dockUi.resting = entries[entries.length - 1].isIntersecting;
        applyDockResting();
      },
      { rootMargin: "0px 0px -8px 0px" }
    );
    io.observe(breakdown);
    // The dock takes no room of its own at the end of the form: a negative
    // bottom margin of its height lays the breakdown under it, so the
    // breakdown slides in beneath the (resting, faded) dock instead of
    // leaving a dock-sized gap. Only with the observer: without it the dock
    // never rests, so it keeps its own room.
    const fit = () => {
      dock.style.marginBottom = `-${dock.offsetHeight}px`;
    };
    fit();
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(fit).observe(dock);
    else window.addEventListener("resize", fit);
  }
  document.getElementById("builderForm")?.addEventListener("focusin", (e) => keepClearOfDock(e.target));
  if (state.edit) setAtcLabels("Update cart");
}

function renderAll() {
  ensureValidSelections();
  renderForm();
  renderPreview();
  schedulePublishSelection();
}

/** Snapshot of the live Build Your Tank selections for chat / integrations. */
function getSelection() {
  if (!state.data || !state.values) return null;
  const v = state.values;
  const metrics = computeMetrics();
  const { qty, options, product: base, promo, discount, total } = priceSummary();
  const offerLines = discount > 0 && !tankPriceUnavailable() ? promoOfferLines(promo) : [];
  const tank = resolveTankSku();
  const addons = [];
  for (const field of TRACKED_ADDON_FIELDS) {
    if (v[field] === "Yes") addons.push(ADDON_LABELS[field] || field);
  }
  if (v.firefit && v.firefit !== "No") addons.push(ADDON_LABELS.firefit || "Fire Fitting");
  if (v.firefit3 === "Yes") addons.push(ADDON_LABELS.firefit3 || "Fire Fitting for Outlet #3");

  const tk = typeKey();
  const size = sizeText();
  const pump = chosenPump();

  const palette = colourPalette();
  // Sizes Kingspan won't paint offer base colours only (the chat lists these).
  const paintable = sizePaintable();
  const baseColours = (palette.base || []).map((c) => c.name).filter(Boolean);
  const paintedColours = paintable ? (palette.painted || []).map((c) => c.name).filter(Boolean) : [];
  const allColours = (palette.all || [])
    .filter((c) => paintable || c.kind !== "painted")
    .map((c) => c.name)
    .filter(Boolean);

  return {
    source: "urban-tank-configurator",
    type: v.type || "",
    typeKey: tk,
    region: v.region || "",
    height: v.height || "",
    width: v.width || "",
    length: v.length || "",
    diameter: v.diameter || "",
    sWidth: v.sWidth || "",
    size,
    colour: v.tankColour || "",
    availableColours: allColours,
    availableBaseColours: baseColours,
    availablePaintedColours: paintedColours,
    // What the size fields list for this shape and footprint (fb-40240: the chat sends it
    // with the [[TANK_CONFIG]] sync and typed messages), and the states offering the shape.
    availableSizes: availableSizeOptions(),
    capacityL: metrics.capacity || 0,
    weightKg: metrics.weight || 0,
    fittings: v.chooseFittingsNow || "",
    inlet: v.inlet || "",
    inlet2: v.inlet2 || "",
    inletPos: v.inletPos || "",
    inlet2Pos: v.inlet2Pos || "",
    overflow: v.overflow || "",
    overflow2: v.overflow2 || "",
    overflowPos: v.overflowPos || "",
    overflow2Pos: v.overflow2Pos || "",
    outlet: v.outlet || "",
    outlet2: v.outlet2 || "",
    outlet3: v.outlet3 || "",
    outletPos: v.outletPos || "",
    outlet2Pos: v.outlet2Pos || "",
    outlet3Pos: v.outlet3Pos || "",
    firefit: v.firefit || "",
    firefit3: v.firefit3 || "",
    firefitPos: v.firefit && v.firefit !== "No" ? v.firefitPos || "" : "",
    firefit3Pos: v.firefit3 === "Yes" ? v.firefit3Pos || "" : "",
    addons,
    // The build's one pump (a PUMPS field) and its cart line name, or "".
    pump,
    pumpLabel: pump ? addonLineLabel(pump) : "",
    qty,
    priceBase: base,
    priceOptions: options,
    // Both offers, per tank (priceTotal already has them off for the quantity):
    // tank = the tank offer, pump = the pump offer (whichever pump it took
    // off); offers = one entry per discounting offer (KingspanTankCore.promoLabels).
    discountAmount: discount,
    promo:
      discount > 0
        ? {
            tank: promo.tank,
            pump: PUMP_FIELDS.reduce((s, f) => s + (Number(promo.addons[f]) || 0), 0),
            addons: { ...promo.addons },
            total: promo.total,
            offers: offerLines,
            label: offerLines.map((l) => l.text).join("; "),
          }
        : null,
    offerText: promoOfferText(offerLines, promo),
    priceTotal: total,
    // The chat reads priceFormatted ("Estimated total: ..."). An unpriceable size must not reach it as the
    // $890 base the total falls back to; the page shows "Price on enquiry" in the same case.
    priceAvailable: !tankPriceUnavailable(),
    priceFormatted: tankPriceUnavailable() ? "Price on enquiry" : money(total),
    sku: tank?.sku || "",
  };
}

/** Compact one-line summary (Connect attributes are ~250 chars each). */
function getSelectionSummary(maxLen = 250) {
  const sel = getSelection();
  if (!sel) return "";
  const parts = [];
  if (sel.type) parts.push(sel.type);
  if (sel.region) parts.push(sel.region);
  if (sel.size) parts.push(sel.size);
  if (sel.capacityL) parts.push(`~${Number(sel.capacityL).toLocaleString("en-AU")}L`);
  if (sel.colour) parts.push(sel.colour);
  if (sel.fittings) parts.push(`fittings:${sel.fittings}`);
  if (sel.addons.length) parts.push(`addons:${sel.addons.slice(0, 3).join(", ")}`);
  if (sel.priceFormatted) parts.push(sel.priceFormatted);
  let out = parts.join(" · ");
  if (out.length > maxLen) out = out.slice(0, maxLen - 1) + "…";
  return out;
}

let _publishSelectionTimer = null;
function schedulePublishSelection() {
  if (_publishSelectionTimer) clearTimeout(_publishSelectionTimer);
  _publishSelectionTimer = setTimeout(publishSelection, 80);
}

function publishSelection() {
  const selection = getSelection();
  if (!selection) return;
  const detail = {
    selection,
    summary: getSelectionSummary(250),
  };
  try {
    window.dispatchEvent(new CustomEvent("kingspan:tank-config-changed", { detail }));
  } catch (err) {
    console.warn("[KUT] publishSelection failed", err);
  }
}

window.KingspanTankConfigurator = {
  getSelection,
  getSummary: getSelectionSummary,
  /** The name an add-on's cart line carries (its Addon property) for this build. */
  addonLineLabel,
  getAvailableColours: () => {
    const sel = getSelection();
    if (!sel) return null;
    return {
      region: sel.region,
      type: sel.type,
      typeKey: sel.typeKey,
      selected: sel.colour,
      all: sel.availableColours || [],
      base: sel.availableBaseColours || [],
      painted: sel.availablePaintedColours || [],
    };
  },
  isReady: () => !!(state.data && state.values),
};

const POSTCODE_STORAGE_KEY = "kut_delivery_postcode";

function storedPostcode() {
  try {
    return localStorage.getItem(POSTCODE_STORAGE_KEY) || "";
  } catch (_) {
    return "";
  }
}

function rememberPostcode(postcode) {
  try {
    localStorage.setItem(POSTCODE_STORAGE_KEY, postcode);
  } catch (_) {
    /* private mode — the estimate still renders for this page */
  }
}

async function deliveryZones() {
  if (state.deliveryZones) return state.deliveryZones;
  if (!shopifyCfg.deliveryZonesUrl) return null;
  try {
    const res = await fetch(shopifyCfg.deliveryZonesUrl);
    if (!res.ok) return null;
    const data = await res.json();
    state.deliveryZones = data?.zones && typeof data.zones === "object" ? data.zones : null;
  } catch (_) {
    state.deliveryZones = null;
  }
  return state.deliveryZones;
}

async function applyDeliveryPostcode(raw) {
  const postcode = String(raw || "").trim();
  if (!/^\d{4}$/.test(postcode)) return false;
  const zones = await deliveryZones();
  if (!zones) {
    // Valid input, but the zones data couldn't load — not the shopper's fault.
    state.delivery = { postcode, unavailable: true };
  } else {
    const entry = zones[postcode];
    state.delivery =
      entry && Number.isFinite(Number(entry.price))
        ? { postcode, zone: entry.zone, price: Number(entry.price) }
        : { postcode, notServiced: true };
  }
  rememberPostcode(postcode);
  renderPreview();
  return true;
}

function bindDeliveryCheck() {
  const input = document.getElementById("kutPostcodeInput");
  const button = document.getElementById("kutPostcodeCheck");
  if (!input || !button) return;
  const run = async () => {
    const ok = await applyDeliveryPostcode(input.value);
    if (!ok) {
      const status = document.getElementById("kutDeliveryStatus");
      if (status) status.textContent = "Enter a 4-digit Australian postcode.";
    }
  };
  button.addEventListener("click", run);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      run();
    }
  });
  const saved = storedPostcode();
  if (saved) {
    input.value = saved;
    applyDeliveryPostcode(saved);
  }
}

/** /cart/add.js error bodies meaning the variant can't be found or sold. */
const VARIANT_NOT_FOUND_RE =
  /cannot find (the )?variant|variant\b[^"]{0,80}\b(not (be )?found|does ?n[o’']t exist)|no such variant/i;

/** A tank that can't be sold at its configured price (see ATC handler). */
class PricingUnavailableError extends Error {
  constructor(message, detail) {
    super(message);
    this.name = "PricingUnavailableError";
    this.detail = detail;
  }
}

/** Inline, accessible notice under the purchase buttons ("" clears it). */
function showAtcNotice(text) {
  let el = document.getElementById("kutAtcNotice");
  if (!text) {
    el?.remove();
    return null;
  }
  if (!el) {
    el = document.createElement("p");
    el.id = "kutAtcNotice";
    el.className = "kut-atc-notice";
    el.setAttribute("role", "alert");
    el.setAttribute("tabindex", "-1");
    const row = document.querySelector("#kut-root .kut__purchase") || document.getElementById("btnAddToCart")?.parentElement;
    row?.insertAdjacentElement("afterend", el);
  }
  el.textContent = text;
  return el;
}

/**
 * Bring the notice into view and focus it. An add from the price dock
 * happens while the purchase row — and so the notice under it — is off-screen.
 */
function revealAtcNotice(el) {
  if (!el?.getBoundingClientRect) return;
  const dock = dockRectOnScreen();
  const visibleBottom = dock && dock.top > 0 ? dock.top : window.innerHeight;
  const r = el.getBoundingClientRect();
  if (r.top < 0 || r.bottom > visibleBottom) {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }
  el.focus({ preventScroll: true });
}

/** Both add-to-cart labels (buy box + price dock) in one place. */
function setAtcLabels(text) {
  for (const id of ["btnAddToCart", "kutStickyAtcBtn"]) {
    const el = document.getElementById(id);
    if (!el) continue;
    const span = el.querySelector("span");
    if (span) span.textContent = text;
    else el.textContent = text;
  }
}

/**
 * Re-render and open the cart drawer with FRESH sections — fetched after all
 * cart mutations (add + any edit-group removal) so it can't show stale lines.
 * Returns false when the theme has no drawer, so callers can fall back to /cart.
 */
async function openCartDrawerFresh() {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer || typeof drawer.renderContents !== "function") return false;
  try {
    const res = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`, {
      credentials: "same-origin",
    });
    if (!res.ok) return false;
    const sections = await res.json();
    if (!sections || !sections["cart-drawer"]) return false;
    // Dawn clears this in product-form's finally; without it a first add from
    // an empty cart keeps the empty-state CSS and breaks the focus trap.
    drawer.classList.remove("is-empty");
    drawer.renderContents({ sections });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Builder vs cart parity, after an add (non-blocking, warns only). The offer
 * is shown from the shop metafield kx_promo.offers and charged by the KPO
 * automatic discounts, so a gap means the two are out of step (a discount
 * changed in the admin without a sync), or the cart carries an old code.
 * Compares this build's _kut_group lines in /cart.js (cents) with the
 * builder's Total and offer for the quantity.
 */
function checkCartParity(groupId, shown) {
  if (!groupId || !shown) return;
  fetch("/cart.js", { credentials: "same-origin" })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const lines = (data?.items || []).filter((it) => it.properties?._kut_group === groupId);
      if (!lines.length) return;
      const sum = (key) => lines.reduce((n, it) => n + (Number(it[key]) || 0), 0);
      const cart = { total: sum("final_line_price"), discount: sum("original_line_price") - sum("final_line_price") };
      const builder = {
        total: Math.round(shown.total * 100),
        discount: Math.round(shown.discountQty * 100),
      };
      if (cart.total !== builder.total || cart.discount !== builder.discount) {
        console.warn("[kut] offer parity: builder and cart differ (cents)", {
          group: groupId,
          builder,
          cart,
          lines: lines.map((it) => ({
            sku: it.sku,
            quantity: it.quantity,
            original: it.original_line_price,
            final: it.final_line_price,
          })),
        });
      }
    })
    .catch(() => {
      /* parity is a diagnostic only */
    });
}

function bindPurchase() {
  const qtyInput = document.getElementById("qtyInput");
  const minus = document.getElementById("qtyMinus");
  const plus = document.getElementById("qtyPlus");
  const syncQty = () => {
    const before = dockSnapshot();
    const prevQty = state.qty;
    state.qty = Math.max(1, Math.floor(Number(qtyInput?.value)) || 1); // whole tanks only
    if (qtyInput) qtyInput.value = String(state.qty);
    renderPreview();
    if (state.qty !== prevQty) noteDockChange(before, "qty", state.qty);
  };
  minus?.addEventListener("click", () => {
    if (qtyInput) qtyInput.value = String(Math.max(1, (Number(qtyInput.value) || 1) - 1));
    syncQty();
  });
  plus?.addEventListener("click", () => {
    if (qtyInput) qtyInput.value = String(Math.max(1, (Number(qtyInput.value) || 1) + 1));
    syncQty();
  });
  qtyInput?.addEventListener("change", syncQty);

  document.getElementById("btnEnquiry")?.addEventListener("click", async (e) => {
    const btn = document.getElementById("btnEnquiry");
    // Dawn embed uses a contact link — allow navigation unless data-copy is set
    if (btn?.tagName === "A" && btn.getAttribute("href") && !btn.hasAttribute("data-copy-payload")) {
      return;
    }
    e.preventDefault();
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    const prev = btn.textContent;
    btn.textContent = "Copied!";
    setTimeout(() => {
      btn.textContent = prev;
    }, 1500);
  });

  document.getElementById("btnAddToCart")?.addEventListener("click", async () => {
    const payload = buildPayload();
    const items = buildCartItems();
    // What the builder shows for this build — checked against the cart after the add
    const shown = priceSummary();
    const btn = document.getElementById("btnAddToCart");
    const prevLabel = btn?.querySelector("span")?.textContent || btn?.textContent || "Add to cart";
    showAtcNotice("");
    if (items.length) {
      try {
        // Pricing integrity: the Build-Your-Own base variant carries a fixed
        // price, so a _fallback_base line would put the tank in the cart for far
        // less than the configured total (e.g. $890 for a $2,970 tank). Never
        // add one — stop and point the shopper to an enquiry instead.
        if (items.some((it) => it?.properties?._fallback_base)) {
          throw new PricingUnavailableError(
            "no size-priced product for this tank",
            { key: typeKey(), values: pickDeepLinkConfig(state.values) }
          );
        }
        if (
          !useBaseVariantCart() &&
          state.skuMap?.mode?.startsWith("tracked_skus") &&
          !resolveTankSku()
        ) {
          throw new Error("Selected tank size is not mapped to a Shopify SKU yet.");
        }
        const { missing: missingAddons } = resolveAddonSkus();
        if (missingAddons.length && !useBaseVariantCart()) {
          throw new Error(
            `These options are not mapped to Shopify products yet: ${missingAddons.join(", ")}`
          );
        }
        if (btn) {
          setAtcLabels(state.edit ? "Updating…" : "Adding…");
          btn.disabled = true;
        }
        let res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ items }),
        });
        // The size's variant can't be sold (archived / unpublished / deleted
        // product): never retry with the fixed-price base variant — that would
        // under-charge. Surface it and route the shopper to an enquiry.
        // Shopify answers 422 or 404 for this, with "Cannot find variant"
        // (or a similar variant-not-found message).
        if (!res.ok) {
          const body = await res.text();
          if ((res.status === 422 || res.status === 404) && VARIANT_NOT_FOUND_RE.test(body)) {
            throw new PricingUnavailableError("tank variant unavailable on the storefront", {
              variant: items[0]?.id,
              sku: resolveTankSku()?.sku,
              status: res.status,
              body: body.slice(0, 200),
            });
          }
          throw new Error(body);
        }
        // Replace-on-edit only after the new group is safely in the cart, so
        // a failed add can never leave the shopper with no tank at all.
        if (state.edit) {
          try {
            await removeEditGroupFromCart();
          } catch (_) {
            alert(
              "Your updated tank was added, but the previous configuration is still in the cart — please remove it there."
            );
          }
        }
        // No discount code: the KPO automatic discount applies the offer in
        // the cart. Check it matches what the builder showed (never blocks).
        checkCartParity(items[0]?.properties?._kut_group, shown);
        // Post-ATC surface: open the cart drawer in place (cross-sell +
        // delivery estimate live there); fall back to the cart page when the
        // theme runs in notification mode or the sections fetch fails.
        if (await openCartDrawerFresh()) {
          if (state.edit) {
            state.edit = null;
            document.getElementById("kutEditBanner")?.remove();
          }
          setAtcLabels("Add to cart");
          if (btn) btn.disabled = false;
          return;
        }
        window.location.href = shopifyCfg.cartUrl || "/cart";
        return;
      } catch (err) {
        if (err instanceof PricingUnavailableError) {
          console.warn("[kut] add to cart blocked:", err.message, err.detail);
          revealAtcNotice(
            showAtcNotice(
              "This tank size can\u2019t be added to the cart online right now. Please use the Send enquiry button and we\u2019ll confirm the price and lead time."
            )
          );
          setAtcLabels(prevLabel);
          if (btn) btn.disabled = false;
          return;
        }
        alert(`Could not ${state.edit ? "update" : "add to"} cart: ${err.message}`);
        setAtcLabels(prevLabel);
        if (btn) btn.disabled = false;
        return;
      }
    }
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setAtcLabels("Config copied");
    setTimeout(() => {
      setAtcLabels(state.edit ? "Update cart" : "Add to cart");
    }, 1500);
  });
}

function buildPayload() {
  const findOpt = (key, name) => state.data.options[key]?.find((o) => o.name === name);
  const region = findOpt("region", state.values.region);
  const type = findOpt("type", state.values.type);
  const attrs = [
    {
      attribute_id: 61401,
      attribute_name: "Choose region:",
      attribute_key: "region",
      attribute_value: { attribute_value_id: region?.id },
      attribute_value_name: region?.name,
      attribute_value_key: region?.key,
    },
    {
      attribute_id: 102335,
      attribute_name: "Choose tank type",
      attribute_key: "type",
      attribute_value: { attribute_value_id: type?.id },
      attribute_value_name: type?.name,
      attribute_value_key: type?.key,
    },
  ];

  for (const [field, attrId, attrKey, label] of [
    ["diameter", 106625, "diameter", "Diameter"],
    ["height", 60411, "height", "Height"],
    ["width", 60410, "width", "Width"],
    ["sWidth", 141146, "sWidth", "Width"],
    ["length", 60412, "length", "Length"],
  ]) {
    if (!visibleDimFields().includes(field)) continue;
    const opt = findOpt(field, state.values[field]);
    attrs.push({
      attribute_id: attrId,
      attribute_name: label,
      attribute_key: attrKey,
      attribute_value: { attribute_value_id: opt?.id },
      attribute_value_name: opt?.name,
      attribute_value_key: opt?.key ?? null,
    });
  }

  const base = state.data.materials.baseColour?.options.find(
    (o) => o.name === state.values.Base_colours
  );
  attrs.push({
    attribute_id: 60467,
    attribute_name: "Base colours",
    attribute_key: null,
    attribute_value: { material_id: base?.id },
    attribute_value_name: base?.name,
    attribute_value_key: null,
  });

  const metrics = computeMetrics();
  return {
    attributes: attrs,
    computed: metrics,
    selections: { ...state.values },
    options_price: optionPriceTotal(),
    locale: "en-US",
  };
}

function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttr(s) {
  return escapeHtml(s).replaceAll("'", "&#39;");
}

function lighten(hex, amount) {
  return mix(hex, "#ffffff", amount);
}

function darken(hex, amount) {
  return mix(hex, "#000000", amount);
}

function mix(a, b, amount) {
  const pa = parseHex(a);
  const pb = parseHex(b);
  if (!pa || !pb) return a;
  const t = Math.min(100, Math.max(0, amount)) / 100;
  const r = Math.round(pa.r + (pb.r - pa.r) * t);
  const g = Math.round(pa.g + (pb.g - pa.g) * t);
  const bl = Math.round(pa.b + (pb.b - pa.b) * t);
  return `#${[r, g, bl].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function parseHex(hex) {
  const m = String(hex || "")
    .replace("#", "")
    .match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

boot().catch((err) => {
  console.error(err);
  // Scope the failure to the configurator container — never blank the page.
  const root = document.getElementById("kut-root") || document.querySelector("[data-kut]");
  if (root) {
    root.innerHTML = `<p class="kut-boot-error" style="padding:2rem">Sorry — the tank builder didn't load (${escapeHtml(
      err.message
    )}). Please refresh the page to try again.</p>`;
  }
});
